import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  PlayIcon,
  StopIcon,
  PauseIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  NoSymbolIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  PencilSquareIcon,
  UserPlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { api } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { AgentDesktopSetup } from '../../../components/admin/AgentDesktopSetup';
import AutoOptimizeControlPanel from '../../../components/admin/AutoOptimizeControlPanel';
import {
  VpsAgentPicker,
  VpsPageHeader,
  applyAgentFormFields,
  fmt,
  formatFollowBuildCounts,
  isHumanizedFollowRunner,
  labelFollowRunner,
  normalizeFollowRunner,
  useRequestGeneration,
  useVpsAgents
} from './vpsShared';
import FollowTargetSignalFilters, { EMPTY_SIGNAL_FILTERS } from './FollowTargetSignalFilters';

const DEFAULT_CONFIG = {
  maxConcurrentBrowsers: 20,
  followsPerAccountMin: 4,
  followsPerAccountMax: 6,
  totalFollowsPerProfileMin: 40,
  totalFollowsPerProfileMax: 60,
  dailyFollowCapPerAccount: 40,
  warmupEnabled: true,
  warmupProfileMinMs: 10_000,
  warmupProfileMaxMs: 45_000,
  warmupHomeMinMs: 20_000,
  warmupHomeMaxMs: 90_000,
  warmupVisitNotifications: true,
  warmupVisitSettings: true,
  browserStaggerBatchSize: 10,
  browserStaggerDelayMinMs: 15_000,
  browserStaggerDelayMaxMs: 20_000,
  delayBetweenAccountsMinMs: 5_000,
  delayBetweenAccountsMaxMs: 15_000,
  followRunner: 'classic',
  accountFollowRunners: {},
  browserEngine: 'gologin',
  roundGapMinMs: 0,
  roundGapMaxMs: 0
};

const FOLLOW_RUNNER_CLASSIC = 'classic';
const FOLLOW_RUNNER_V3 = 'v3';
const FOLLOW_RUNNER_H2 = 'h2';

/**
 * Placeholder rows so the age picker renders before counts arrive. Mirrors
 * MAX_BUCKET in backend/utils/followTargetAgeFilter.js — bucket 10 is "10+".
 */
const MAX_AGE_BUCKET = 10;
const DEFAULT_AGE_BUCKETS = Array.from({ length: MAX_AGE_BUCKET + 1 }, (_, bucket) => ({
  bucket,
  label: bucket === 0
    ? 'Under 1 year'
    : bucket === MAX_AGE_BUCKET
      ? '10+ years'
      : `${bucket}\u2013${bucket + 1} years`,
  available: null
}));

/** The whole range, which is what the age picker starts on. */
const FULL_AGE_RANGE = DEFAULT_AGE_BUCKETS.map((b) => b.bucket);

function ageBucketLabel(bucket) {
  return DEFAULT_AGE_BUCKETS[bucket]?.label || '';
}

const SOURCE_TYPE_LABELS = {
  scrape_v2_pool: 'Scrape V2 pool',
  scrape_v2_session: 'Scrape V2 session',
  scrape_tag: 'Scrape tag',
  paste: 'Paste list'
};

function SettingsDrawer({ open, onToggle, title, subtitle, children }) {
  return (
    <div className="border-t border-gray-100 dark:border-gray-800">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-3 py-3 text-left"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2 min-w-0">
          {open
            ? <ChevronDownIcon className="w-4 h-4 text-gray-500 shrink-0" />
            : <ChevronRightIcon className="w-4 h-4 text-gray-500 shrink-0" />}
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-gray-500">{title}</div>
            {subtitle ? (
              <div className="text-xs text-gray-500 mt-0.5 truncate">{subtitle}</div>
            ) : null}
          </div>
        </div>
        <span className="text-xs text-indigo-600 dark:text-indigo-400 shrink-0">
          {open ? 'Close' : 'Open'}
        </span>
      </button>
      {open && <div className="pb-3">{children}</div>}
    </div>
  );
}

/** Contiguous bucket list for a from–to range, inclusive of both ends. */
function ageRangeToBuckets(from, to) {
  const lo = Math.min(from, to);
  const hi = Math.max(from, to);
  return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
}

/** Mirrors ALL_PLATFORMS / PLATFORM_LABELS in backend/utils/followTargetPlatformFilter.js. */
const DEFAULT_PLATFORMS = [
  { platform: 'android', label: 'Android', available: null },
  { platform: 'ios', label: 'Apple / iOS', available: null },
  { platform: 'web', label: 'Web', available: null },
  { platform: 'unknown', label: 'Other', available: null }
];

const CLASSIC_MODULES = {
  follow: { enabled: true },
  warmup: { enabled: true },
  post: { enabled: false },
  unfollow: { enabled: false },
  probe: { enabled: false }
};

const FOLLOW_TEST_GROUP_CONFIG_KEYS = [
  'maxConcurrentBrowsers',
  'followsPerAccountMin',
  'followsPerAccountMax',
  'totalFollowsPerProfileMin',
  'totalFollowsPerProfileMax',
  'roundGapMinMs',
  'roundGapMaxMs'
];

function parseUsernameQuery(q) {
  return String(q || '')
    .split(/[\s,;]+/)
    .map((s) => s.replace(/^@/, '').trim().toLowerCase())
    .filter(Boolean);
}

function pickGroupConfig(config) {
  const out = {};
  for (const key of FOLLOW_TEST_GROUP_CONFIG_KEYS) {
    const n = Number(config?.[key]);
    if (Number.isFinite(n) && n >= 0) out[key] = n;
  }
  return out;
}

function newGroupId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `g-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function countFollowBuilds(ids, map) {
  let classic = 0;
  let v3 = 0;
  let h2 = 0;
  for (const id of ids || []) {
    const r = normalizeFollowRunner(map?.[id]);
    if (r === FOLLOW_RUNNER_H2) h2 += 1;
    else if (r === FOLLOW_RUNNER_V3) v3 += 1;
    else classic += 1;
  }
  return { classic, v3, h2, none: 0, total: (ids || []).length };
}

/** Catalog feel when the account has a user-profile; otherwise "no user-profile". */
function countRunBuildsFromProfiles(ids, accountById, profiles) {
  const byProfile = new Map((profiles || []).map((p) => [String(p._id), p]));
  let classic = 0;
  let v3 = 0;
  let h2 = 0;
  let none = 0;
  for (const raw of ids || []) {
    const row = accountById.get(String(raw));
    const pid = row?.userProfileId ? String(row.userProfileId) : '';
    const profile = pid ? byProfile.get(pid) : null;
    if (!profile) {
      none += 1;
      continue;
    }
    const r = normalizeFollowRunner(profile.followRunner);
    if (r === FOLLOW_RUNNER_H2) h2 += 1;
    else if (r === FOLLOW_RUNNER_V3) v3 += 1;
    else classic += 1;
  }
  return { classic, v3, h2, none, total: (ids || []).length };
}

function groupBuildCounts(group) {
  return countFollowBuilds(group?.accountIds, group?.accountFollowRunners);
}

/** Logged-in accounts on this VPS that belong to the ticked user-profiles. */
function accountsForUserProfiles(profileIds, rows, profiles, loggedInIdSet) {
  const idSet = new Set((profileIds || []).map(String));
  const byProfile = new Map((profiles || []).map((p) => [String(p._id), p]));
  const ids = [];
  const seen = new Set();
  const runners = {};
  for (const row of rows || []) {
    const accountId = String(row.accountId);
    if (!loggedInIdSet.has(accountId)) continue;
    const pid = String(row.userProfileId || '');
    if (!pid || !idSet.has(pid)) continue;
    if (seen.has(accountId)) continue;
    seen.add(accountId);
    ids.push(accountId);
    const profile = byProfile.get(pid);
    runners[accountId] = normalizeFollowRunner(profile?.followRunner);
  }
  return { ids, runners };
}

const RUN_PROFILES_STORAGE = 'vps-automations-run-profiles';

function runProfilesStorageKey(agentId) {
  return `${RUN_PROFILES_STORAGE}:${agentId}`;
}

function loadRunProfileIds(agentId) {
  if (!agentId) return [];
  try {
    const raw = window.localStorage.getItem(runProfilesStorageKey(agentId));
    const ids = raw ? JSON.parse(raw) : [];
    return Array.isArray(ids) ? ids.map(String).filter(Boolean) : [];
  } catch {
    return [];
  }
}

function saveRunProfileIds(agentId, ids) {
  if (!agentId) return;
  try {
    window.localStorage.setItem(
      runProfilesStorageKey(agentId),
      JSON.stringify((ids || []).map(String))
    );
  } catch {
    /* ignore quota / private mode */
  }
}

function mergeGroupRunSelection(groups, loggedInIdSet) {
  const ids = [];
  const seen = new Set();
  const accountFollowRunners = {};
  for (const group of groups || []) {
    for (const raw of group.accountIds || []) {
      const id = String(raw);
      if (!loggedInIdSet.has(id)) continue;
      if (!seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
      accountFollowRunners[id] = normalizeFollowRunner(group.accountFollowRunners?.[id]);
    }
  }
  return { ids, accountFollowRunners };
}

function minutesFromMsValue(ms) {
  return Math.round((Number(ms) || 0) / 60_000);
}

function buildGroupEditDraft(group, accountById, tab = 'settings') {
  const config = {
    ...pickGroupConfig(DEFAULT_CONFIG),
    ...pickGroupConfig(group?.config)
  };
  const rows = (group?.accountIds || []).map((id) => {
    const sid = String(id);
    const row = accountById.get(sid);
    return {
      accountId: sid,
      username: row?.username || sid.slice(-6),
      runner: normalizeFollowRunner(group?.accountFollowRunners?.[sid])
    };
  });
  return {
    id: group.id,
    name: group.name || '',
    config,
    rows,
    addPaste: '',
    tab
  };
}

function FollowTestGroupEditor({
  draft,
  onChange,
  loggedInAccounts,
  busy,
  onSave,
  onCancel
}) {
  const setConfigKey = (key, value) => {
    onChange({ ...draft, config: { ...draft.config, [key]: value } });
  };
  const setGapMinutes = (key, minutes) => {
    const n = Math.max(0, Number(minutes) || 0) * 60_000;
    const config = { ...draft.config, [key]: n };
    if (key === 'roundGapMinMs' && config.roundGapMaxMs < config.roundGapMinMs) {
      config.roundGapMaxMs = config.roundGapMinMs;
    }
    if (key === 'roundGapMaxMs' && config.roundGapMaxMs < config.roundGapMinMs) {
      config.roundGapMinMs = config.roundGapMaxMs;
    }
    onChange({ ...draft, config });
  };
  const addFromPaste = () => {
    const tokens = parseUsernameQuery(draft.addPaste);
    if (!tokens.length) {
      toast.error('Paste @usernames that are logged in on this VPS');
      return;
    }
    const haveId = new Set(draft.rows.map((r) => String(r.accountId)));
    const haveName = new Set(draft.rows.map((r) => String(r.username || '').toLowerCase()));
    const byName = new Map(
      (loggedInAccounts || []).map((r) => [String(r.username || '').toLowerCase(), r])
    );
    const v3Count = draft.rows.filter((r) => r.runner === FOLLOW_RUNNER_V3).length;
    const defaultRunner = v3Count >= draft.rows.length - v3Count
      ? FOLLOW_RUNNER_V3
      : FOLLOW_RUNNER_CLASSIC;
    const nextRows = [...draft.rows];
    const missing = [];
    let added = 0;
    for (const token of tokens) {
      if (haveName.has(token)) continue;
      const row = byName.get(token);
      if (!row) {
        missing.push(token);
        continue;
      }
      const id = String(row.accountId);
      if (haveId.has(id)) continue;
      haveId.add(id);
      haveName.add(token);
      nextRows.push({
        accountId: id,
        username: row.username,
        runner: defaultRunner
      });
      added += 1;
    }
    onChange({
      ...draft,
      rows: nextRows,
      addPaste: missing.length ? missing.map((u) => `@${u}`).join('\n') : ''
    });
    if (added) toast.success(`Added ${added} account${added === 1 ? '' : 's'}`);
    if (missing.length) {
      toast.error(`Not logged in on this VPS: ${missing.map((u) => `@${u}`).join(', ')}`);
    }
    if (!added && !missing.length) toast.success('Those accounts are already in the group');
  };

  return (
    <div className="rounded-xl border border-blue-300 dark:border-blue-700 bg-white dark:bg-gray-900 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="text-xs uppercase tracking-wide text-gray-500">Edit group</div>
          <input
            type="text"
            className="form-input text-sm w-full max-w-sm"
            value={draft.name}
            maxLength={80}
            disabled={busy}
            onChange={(e) => onChange({ ...draft, name: e.target.value })}
          />
        </div>
        <button
          type="button"
          className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          title="Close"
          onClick={onCancel}
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      <div className="flex gap-1.5">
        <button
          type="button"
          className={`!py-1 !px-2.5 text-xs ${draft.tab === 'settings' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => onChange({ ...draft, tab: 'settings' })}
        >
          Automation
        </button>
        <button
          type="button"
          className={`!py-1 !px-2.5 text-xs ${draft.tab === 'accounts' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => onChange({ ...draft, tab: 'accounts' })}
        >
          Accounts ({draft.rows.length})
        </button>
      </div>

      {draft.tab === 'settings' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <label className="text-xs space-y-1">
            <span className="text-gray-500 uppercase tracking-wide">Max browsers</span>
            <input
              type="number"
              min={1}
              max={1000}
              className="form-input text-sm w-full"
              value={draft.config.maxConcurrentBrowsers ?? ''}
              onChange={(e) => setConfigKey('maxConcurrentBrowsers', Number(e.target.value) || 1)}
            />
          </label>
          <label className="text-xs space-y-1">
            <span className="text-gray-500 uppercase tracking-wide">Round follows min–max</span>
            <div className="flex gap-2">
              <input
                type="number"
                className="form-input text-sm w-full"
                value={draft.config.followsPerAccountMin ?? ''}
                onChange={(e) => setConfigKey('followsPerAccountMin', Number(e.target.value) || 1)}
              />
              <input
                type="number"
                className="form-input text-sm w-full"
                value={draft.config.followsPerAccountMax ?? ''}
                onChange={(e) => setConfigKey('followsPerAccountMax', Number(e.target.value) || 1)}
              />
            </div>
          </label>
          <label className="text-xs space-y-1">
            <span className="text-gray-500 uppercase tracking-wide">Daily total min–max</span>
            <div className="flex gap-2">
              <input
                type="number"
                className="form-input text-sm w-full"
                value={draft.config.totalFollowsPerProfileMin ?? ''}
                onChange={(e) => setConfigKey('totalFollowsPerProfileMin', Number(e.target.value) || 1)}
              />
              <input
                type="number"
                className="form-input text-sm w-full"
                value={draft.config.totalFollowsPerProfileMax ?? ''}
                onChange={(e) => setConfigKey('totalFollowsPerProfileMax', Number(e.target.value) || 1)}
              />
            </div>
          </label>
          <label className="text-xs space-y-1">
            <span className="text-gray-500 uppercase tracking-wide">Wait between rounds (min)</span>
            <div className="flex gap-2">
              <input
                type="number"
                min={0}
                className="form-input text-sm w-full"
                value={minutesFromMsValue(draft.config.roundGapMinMs)}
                onChange={(e) => setGapMinutes('roundGapMinMs', e.target.value)}
              />
              <input
                type="number"
                min={0}
                className="form-input text-sm w-full"
                value={minutesFromMsValue(draft.config.roundGapMaxMs)}
                onChange={(e) => setGapMinutes('roundGapMaxMs', e.target.value)}
              />
            </div>
          </label>
        </div>
      )}

      {draft.tab === 'accounts' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs space-y-1 flex-1 min-w-[16rem]">
              <span className="text-gray-500 uppercase tracking-wide">Add logged-in accounts</span>
              <textarea
                className="form-input text-sm w-full min-h-[4.5rem]"
                placeholder="Paste @usernames"
                value={draft.addPaste}
                disabled={busy}
                onChange={(e) => onChange({ ...draft, addPaste: e.target.value })}
              />
            </label>
            <button
              type="button"
              className="btn-secondary !py-2 !px-3 text-sm"
              disabled={busy}
              onClick={addFromPaste}
            >
              Add to group
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5 text-xs">
            <button
              type="button"
              className="btn-secondary !py-1 !px-2"
              disabled={!draft.rows.length}
              onClick={() => onChange({
                ...draft,
                rows: draft.rows.map((r) => ({ ...r, runner: FOLLOW_RUNNER_CLASSIC }))
              })}
            >
              All URL follow
            </button>
            <button
              type="button"
              className="btn-secondary !py-1 !px-2"
              disabled={!draft.rows.length}
              onClick={() => onChange({
                ...draft,
                rows: draft.rows.map((r) => ({ ...r, runner: FOLLOW_RUNNER_V3 }))
              })}
            >
              All Humanized 1
            </button>
            <button
              type="button"
              className="btn-secondary !py-1 !px-2"
              disabled={!draft.rows.length}
              onClick={() => onChange({
                ...draft,
                rows: draft.rows.map((r) => ({ ...r, runner: FOLLOW_RUNNER_H2 }))
              })}
            >
              All Humanized 2
            </button>
          </div>
          <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
            {draft.rows.map((row) => (
              <div key={row.accountId} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                <span className="font-mono flex-1 min-w-0 truncate">@{row.username}</span>
                <select
                  className="form-select text-xs py-1 w-40"
                  value={row.runner}
                  onChange={(e) => onChange({
                    ...draft,
                    rows: draft.rows.map((r) => (
                      r.accountId === row.accountId
                        ? { ...r, runner: normalizeFollowRunner(e.target.value) }
                        : r
                    ))
                  })}
                >
                  <option value={FOLLOW_RUNNER_CLASSIC}>URL follow</option>
                  <option value={FOLLOW_RUNNER_V3}>Humanized 1</option>
                  <option value={FOLLOW_RUNNER_H2}>Humanized 2</option>
                </select>
                <button
                  type="button"
                  className="text-gray-400 hover:text-red-500"
                  title={`Remove @${row.username}`}
                  onClick={() => onChange({
                    ...draft,
                    rows: draft.rows.filter((r) => r.accountId !== row.accountId)
                  })}
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
            {!draft.rows.length && (
              <div className="px-3 py-4 text-xs text-gray-500 text-center">
                No accounts in this group yet.
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-2 pt-1">
        <button type="button" className="btn-secondary !py-1.5 !px-3 text-sm" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn-primary !py-1.5 !px-3 text-sm" disabled={busy} onClick={onSave}>
          Save group
        </button>
      </div>
    </div>
  );
}

/** Live jobs poll faster; idle agents much slower. */
const POLL_LIVE_MS = 5_000;
const POLL_IDLE_MS = 15_000;
const OVERVIEW_POLL_MS = 12_000;
/** Full pool stats (available / already followed) every N light polls. */
const FULL_STATS_EVERY = 4;

function statusTone(status) {
  switch (String(status || '')) {
    case 'running':
    case 'warmup':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
    case 'daily_limit':
    case 'round_done':
      return 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200';
    case 'captcha':
    case 'auth_expired':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
    case 'suspended':
    case 'failed':
      return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
    case 'paused':
    case 'pausing':
      return 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  }
}

function isLiveAgentStatus(status) {
  return ['running', 'pausing', 'warmup', 'paused', 'preparing'].includes(String(status || ''));
}

/** Automations list: logged-in only. Captcha / auth_expired stay on the agent and return when active. */
function isLoggedInAutomationAccount(row) {
  return String(row?.profileStatus || 'active').toLowerCase() === 'active';
}

function isLiveJobRosterRow(row) {
  if (row?.inJob === true) return true;
  if (row?.inJob === false) return false;
  return Number(row?.budgetTotal) > 0
    || Number(row?.followed) > 0
    || (row?.jobStatus && row.jobStatus !== 'queued');
}

function DailyGoalMeter({ atCap = 0, total = 0 }) {
  const cap = Number(atCap) || 0;
  const tot = Number(total) || 0;
  const pct = tot ? Math.min(100, Math.round((cap / tot) * 100)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="text-xs text-gray-500 tabular-nums">
          {fmt(cap)} / {fmt(tot)} accounts at cap
        </span>
        <span className="text-xs text-gray-400 tabular-nums">{pct}%</span>
      </div>
      <div className="h-3 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-sky-500 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function LiveAccountTable({ rows, emptyLabel }) {
  return (
    <table className="min-w-full text-sm">
      <thead className="bg-gray-50 dark:bg-gray-800/60 sticky top-0">
        <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
          <th className="px-4 py-2 font-medium">Account</th>
          <th className="px-4 py-2 font-medium">Status</th>
          <th className="px-4 py-2 font-medium">Followed</th>
          <th className="px-4 py-2 font-medium">Budget left</th>
          <th className="px-4 py-2 font-medium">Error</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
        {rows.map((row) => (
          <tr key={row.accountId}>
            <td className="px-4 py-2 font-mono">@{row.username}</td>
            <td className="px-4 py-2">
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${statusTone(row.jobStatus)}`}>
                {row.jobStatus}
              </span>
            </td>
            <td className="px-4 py-2 tabular-nums">
              {fmt(row.followed)}
              {row.budgetTotal > 0 && (
                <span className="text-gray-400"> / {fmt(row.budgetTotal)}</span>
              )}
            </td>
            <td className="px-4 py-2 tabular-nums">{fmt(row.budgetRemaining)}</td>
            <td className="px-4 py-2 text-xs text-gray-500 max-w-[16rem] truncate" title={row.lastError || ''}>
              {row.lastError || '—'}
            </td>
          </tr>
        ))}
        {!rows.length && (
          <tr>
            <td colSpan={5} className="px-4 py-6 text-center text-gray-500 text-xs">
              {emptyLabel}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/**
 * Pool-only option: take another model's aged follow-backs before the scrape
 * cohorts. The follow model itself is omitted — those rows are already deduped.
 */
function FollowBacksFirstFields({
  enabled,
  onEnabled,
  sourceModelId,
  onSourceModelId,
  minAgeDays,
  onMinAgeDays,
  models,
  modelLabel,
}) {
  return (
    <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          className="form-checkbox h-3.5 w-3.5"
          checked={Boolean(enabled)}
          onChange={(e) => onEnabled(e.target.checked)}
        />
        <span className="text-sm text-gray-800 dark:text-gray-100">
          Follow another model&apos;s follow-backs first
        </span>
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-xs space-y-1">
          <span className="text-gray-500 uppercase tracking-wide">Source model</span>
          <select
            className="form-select text-sm w-full"
            value={sourceModelId || ''}
            disabled={!enabled}
            onChange={(e) => onSourceModelId(e.target.value)}
          >
            <option value="">Select model…</option>
            {(models || []).map((m) => (
              <option key={m._id} value={m._id}>
                {modelLabel(m)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs space-y-1">
          <span className="text-gray-500 uppercase tracking-wide">Minimum age (days)</span>
          <input
            type="number"
            min={1}
            max={90}
            disabled={!enabled}
            className="form-input text-sm w-full"
            value={minAgeDays}
            onChange={(e) => onMinAgeDays(e.target.value)}
          />
        </label>
      </div>
      <p className="text-[11px] text-gray-400">
        Users who followed the source model back are dispatched first, once they
        are at least this many days old. Anyone this model already followed is
        skipped. The scrape pool fills whatever is left.
      </p>
    </div>
  );
}

/**
 * Two-handle slider over the age bands. Each handle is a real range input, so
 * it is draggable and arrow-key steppable, and neither can cross the other.
 */
function AgeRangeSlider({ from, to, max, onChange }) {
  // Measured against the thumb-inset span the track uses, not the full width.
  const offset = (value) => `calc(0.5rem + ${(value / max) * 100} * (100% - 1rem) / 100)`;
  return (
    <div className="range-slider">
      <div className="range-slider-track" />
      <div
        className="range-slider-fill"
        style={{ left: offset(from), right: offset(max - to) }}
      />
      <input
        type="range"
        min={0}
        max={max}
        step={1}
        value={from}
        aria-label="Youngest account age to include"
        // Once both handles sit on the maximum the lower one would be buried
        // under the upper one and unreachable by mouse, so lift it above.
        style={{ zIndex: from >= max ? 4 : 2 }}
        onChange={(e) => onChange(Math.min(Number(e.target.value), to), to)}
      />
      <input
        type="range"
        min={0}
        max={max}
        step={1}
        value={to}
        aria-label="Oldest account age to include"
        style={{ zIndex: 3 }}
        onChange={(e) => onChange(from, Math.max(Number(e.target.value), from))}
      />
    </div>
  );
}

const ACCOUNT_LIST_PREVIEW = 50;

function AccountListFoldBar({ label, onClick }) {
  return (
    <div className="px-4 py-2.5 flex justify-center border-t border-gray-100 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-800/40">
      <button
        type="button"
        className="btn-secondary !py-1 !px-3 text-xs"
        onClick={onClick}
      >
        {label}
      </button>
    </div>
  );
}

export default function VpsAutomations() {
  const { user } = useAuth();
  const {
    agents,
    selectedId,
    selectAgent,
    isAllAgents,
    loading: agentsLoading,
    reload: reloadAgents
  } = useVpsAgents({ allowAll: true });
  const { bump, isCurrent, genRef } = useRequestGeneration();
  const [dash, setDash] = useState(null);
  const [overview, setOverview] = useState(null);
  const [models, setModels] = useState([]);
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [sourceType, setSourceType] = useState('scrape_v2_pool');
  const [workspaceId, setWorkspaceId] = useState('legacy');
  const [workspaces, setWorkspaces] = useState([]);
  const [scrapeTag, setScrapeTag] = useState('');
  const [followModelId, setFollowModelId] = useState('');
  const [fbfEnabled, setFbfEnabled] = useState(false);
  const [fbfSourceModelId, setFbfSourceModelId] = useState('');
  const [fbfMinAgeDays, setFbfMinAgeDays] = useState(7);
  const [ageBuckets, setAgeBuckets] = useState(FULL_AGE_RANGE);
  const [includeUnknownAge, setIncludeUnknownAge] = useState(true);
  const [platforms, setPlatforms] = useState([]);
  const [includeUnknownPlatform, setIncludeUnknownPlatform] = useState(true);
  const [signalFilters, setSignalFilters] = useState(EMPTY_SIGNAL_FILTERS);
  const isCleanSheetWorkspace = Boolean(workspaceId && workspaceId !== 'legacy');
  const activeSignalFilters = isCleanSheetWorkspace ? signalFilters : EMPTY_SIGNAL_FILTERS;
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [showAccounts, setShowAccounts] = useState(true);
  const [accountsListExpanded, setAccountsListExpanded] = useState(false);
  const accountsSectionRef = useRef(null);
  // Subset of enabled accounts to start this job (does not deselect the rest).
  const [runAccountIds, setRunAccountIds] = useState([]);
  const runAccountsInitRef = useRef('');
  const fetchedProfilesRef = useRef(new Set());
  const [accountSearch, setAccountSearch] = useState('');
  const [followTestGroups, setFollowTestGroups] = useState([]);
  const [userProfiles, setUserProfiles] = useState([]);
  const [selectedProfileIds, setSelectedProfileIds] = useState(() => {
    try {
      const agentId = window.localStorage.getItem('wingr.vps.selectedAgentId') || '';
      if (!agentId || agentId === 'all') return [];
      return loadRunProfileIds(agentId);
    } catch {
      return [];
    }
  });
  const skipRunProfilesPersistRef = useRef(true);
  const [autoAssignAmount, setAutoAssignAmount] = useState(80);
  const [assignByProfile, setAssignByProfile] = useState({});
  const [assignBusy, setAssignBusy] = useState(false);
  const [groupNameDraft, setGroupNameDraft] = useState('');
  const [activeGroupId, setActiveGroupId] = useState('');
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [editDraft, setEditDraft] = useState(null);
  const [groupsBusy, setGroupsBusy] = useState(false);
  // Accounts that have sent 100 follows since their last follow-back.
  const [excludeCold, setExcludeCold] = useState(false);
  const [coldBusy, setColdBusy] = useState(false);
  const [configureOpen, setConfigureOpen] = useState(false);
  const [followSettingsOpen, setFollowSettingsOpen] = useState(false);
  const [targetFiltersOpen, setTargetFiltersOpen] = useState(false);
  const [autoOptimizeOpen, setAutoOptimizeOpen] = useState(false);
  // Auto-optimize follow automation — top-N + explore/exploit + optional
  // scraper gating with passive re-score. Persisted onto
  // agent.targetSource.autoOptimize by the target-source PATCH.
  const [autoOptEnabled, setAutoOptEnabled] = useState(false);
  const [autoOptMetric, setAutoOptMetric] = useState('followback');
  const [autoOptTopN, setAutoOptTopN] = useState(50);
  const [autoOptExplorationRatio, setAutoOptExplorationRatio] = useState(0.2);
  const [autoOptMinSampleFollows, setAutoOptMinSampleFollows] = useState(300);
  const [autoOptCoolDownFloor, setAutoOptCoolDownFloor] = useState(3);
  const [autoOptRefreshMinutes, setAutoOptRefreshMinutes] = useState(30);
  const [autoOptFallback, setAutoOptFallback] = useState(false);
  const [autoOptGateEnabled, setAutoOptGateEnabled] = useState(false);
  const [autoOptGatePauseAfter, setAutoOptGatePauseAfter] = useState(400);
  const [autoOptGatePauseBelow, setAutoOptGatePauseBelow] = useState(1.75);
  const [autoOptGateResumeAt, setAutoOptGateResumeAt] = useState(2.0);
  const [autoOptGateRescoreHours, setAutoOptGateRescoreHours] = useState(6);
  const [autoOptGateExcludeDays, setAutoOptGateExcludeDays] = useState(60);
  const [autoOptGateExcludeUnder, setAutoOptGateExcludeUnder] = useState(1.0);
  const [autoOptSummary, setAutoOptSummary] = useState(null);
  // Bounded-spend lifecycle. Supersedes the top/explore split above: each
  // source gets a lifetime follow budget charged at claim time, then a
  // verdict once its follow-backs have had time to land.
  const [lifeEnabled, setLifeEnabled] = useState(false);
  const [lifeTrialSlots, setLifeTrialSlots] = useState(20);
  const [lifeMaxInTest, setLifeMaxInTest] = useState(2000);
  const [lifeTrialBudget, setLifeTrialBudget] = useState(300);
  const [lifeExtensionBudget, setLifeExtensionBudget] = useState(700);
  const [lifeMaturityHours, setLifeMaturityHours] = useState(6);
  const [lifePromoteAt, setLifePromoteAt] = useState(3.0);
  const [lifeParkBelow, setLifeParkBelow] = useState(1.5);
  const [lifeDemoteBelow, setLifeDemoteBelow] = useState(1.5);
  const [lifeMinVerdictFollows, setLifeMinVerdictFollows] = useState(50);
  const [lifeProvenWindow, setLifeProvenWindow] = useState(7);
  const [lifeRetestDays, setLifeRetestDays] = useState(30);
  const [lifeRescrapeBelow, setLifeRescrapeBelow] = useState(0);
  const [lifeWilson, setLifeWilson] = useState(true);
  const [lifeSummary, setLifeSummary] = useState(null);

  /**
   * Lifecycle slice of the targetSource PATCH. Both save paths (Start and
   * Apply filters) send the same shape so the two cannot drift apart.
   */
  const buildLifecyclePayload = () => ({
    enabled: lifeEnabled,
    trialSlots: lifeTrialSlots,
    maxInTest: lifeMaxInTest,
    trialBudget: lifeTrialBudget,
    trialExtensionBudget: lifeExtensionBudget,
    maturityHours: lifeMaturityHours,
    promoteAtPct: lifePromoteAt,
    parkBelowPct: lifeParkBelow,
    demoteBelowPct: lifeDemoteBelow,
    minVerdictFollows: lifeMinVerdictFollows,
    provenWindowDays: lifeProvenWindow,
    parkedRetestDays: lifeRetestDays,
    rescrapeWhenRemainingBelow: lifeRescrapeBelow,
    useWilsonBounds: lifeWilson,
  });
  const appliedAgentRef = useRef('');
  const configFromServerRef = useRef('');
  const pollCountRef = useRef(0);
  const modelsLoadedForRef = useRef('');
  const liveRef = useRef(false);
  // Held in a ref so the polling callbacks can read the newest selection
  // without their identity changing on every slider drag.
  const targetFilterParamsRef = useRef(null);

  useEffect(() => {
    api.get('/scraper-v2/workspaces')
      .then((res) => {
        if (res.data?.success) setWorkspaces(res.data.workspaces || []);
      })
      .catch(() => {});
  }, []);

  const loadOverview = useCallback(async () => {
    const gen = bump();
    try {
      const res = await api.get('/admin/va/ai-agents/automations-overview');
      if (!isCurrent(gen)) return;
      if (res.data?.success) setOverview(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [bump, isCurrent]);

  const loadAccountsForProfiles = useCallback(async (profileIds) => {
    if (!selectedId || isAllAgents) return [];
    const ids = (profileIds || []).map(String).filter(Boolean);
    if (!ids.length) return [];
    const gen = genRef.current;
    const agentId = selectedId;
    try {
      const res = await api.get(`/admin/va/ai-agents/${agentId}/accounts-for-profiles`, {
        params: { profileIds: ids.join(',') }
      });
      if (!isCurrent(gen) || agentId !== selectedId) return [];
      const rows = res.data?.accounts || [];
      setDash((prev) => {
        if (prev && String(prev.agent?._id) !== String(agentId)) return prev;
        const base = prev || { agent: { _id: agentId }, parkedAccounts: [], totals: {} };
        const byId = new Map((base.accounts || []).map((r) => [String(r.accountId), r]));
        for (const row of rows) {
          const id = String(row.accountId);
          byId.set(id, { ...(byId.get(id) || {}), ...row });
        }
        return { ...base, accounts: [...byId.values()] };
      });
      return rows;
    } catch (err) {
      console.error(err);
      return [];
    }
  }, [selectedId, isAllAgents, genRef, isCurrent]);

  const loadDashboard = useCallback(async ({ light = true, poolStats, isolate = false } = {}) => {
    if (!selectedId || isAllAgents) {
      setDash(null);
      return;
    }
    const gen = isolate ? genRef.current : bump();
    const agentId = selectedId;
    const wantPoolStats = poolStats == null ? !light : Boolean(poolStats);
    try {
      const res = await api.get(`/admin/va/ai-agents/${agentId}/automation-dashboard`, {
        params: {
          ...(light ? { light: 1 } : {}),
          poolStats: wantPoolStats ? 1 : 0,
          // Preview the pickers as they stand, so "Available to follow" answers
          // for the selection on screen rather than the last saved one.
          ...(wantPoolStats ? (targetFilterParamsRef.current || {}) : {})
        }
      });
      if (!isCurrent(gen) || agentId !== selectedId) return;
      if (!res.data?.success) return;

      setDash((prev) => {
        if (light && prev && String(prev.agent?._id) === String(res.data.agent?._id)) {
          return {
            ...res.data,
            // Light polls omit account rows — keep the last full table.
            accounts: (res.data.accounts && res.data.accounts.length)
              ? res.data.accounts
              : (prev.accounts || []),
            parkedAccounts: (res.data.parkedAccounts && res.data.parkedAccounts.length)
              ? res.data.parkedAccounts
              : (prev.parkedAccounts || []),
            totals: {
              ...prev.totals,
              ...res.data.totals,
              // Keep prior pool numbers when this response skipped poolStats.
              available: res.data.totals?.available ?? prev.totals?.available ?? null,
              alreadyFollowed: res.data.totals?.alreadyFollowed ?? prev.totals?.alreadyFollowed ?? null,
              storedInSource: res.data.totals?.storedInSource ?? prev.totals?.storedInSource ?? null,
              claimedInSource: res.data.totals?.claimedInSource ?? prev.totals?.claimedInSource ?? null,
              followsSent: res.data.totals?.followsSent ?? prev.totals?.followsSent ?? 0
            }
          };
        }
        if (!wantPoolStats && prev && String(prev.agent?._id) === String(res.data.agent?._id)) {
          return {
            ...res.data,
            totals: {
              ...res.data.totals,
              available: res.data.totals?.available ?? prev.totals?.available ?? null,
              alreadyFollowed: res.data.totals?.alreadyFollowed ?? prev.totals?.alreadyFollowed ?? null,
              storedInSource: res.data.totals?.storedInSource ?? prev.totals?.storedInSource ?? null,
              claimedInSource: res.data.totals?.claimedInSource ?? prev.totals?.claimedInSource ?? null
            }
          };
        }
            return res.data;
      });

      // Authoritative form sync once per agent selection (not on every poll).
      if (configFromServerRef.current !== agentId) {
        configFromServerRef.current = agentId;
        setConfig({ ...DEFAULT_CONFIG, ...(res.data.agent?.config || {}) });
        setSourceType(res.data.agent?.targetSource?.type || 'scrape_v2_pool');
        setWorkspaceId(res.data.agent?.targetSource?.workspaceId || 'legacy');
        setScrapeTag(res.data.agent?.targetSource?.scrapeTag || '');
        setFollowModelId(res.data.agent?.followModelId ? String(res.data.agent.followModelId) : '');
        const fbf = res.data.agent?.targetSource?.followBacksFirst || {};
        setFbfEnabled(Boolean(fbf.enabled));
        setFbfSourceModelId(fbf.sourceModelId ? String(fbf.sourceModelId) : '');
        setFbfMinAgeDays(Number(fbf.minAgeDays) || 7);
        const savedBuckets = res.data.agent?.targetSource?.ageBuckets;
        const parsedBuckets = Array.isArray(savedBuckets)
          ? savedBuckets.map(Number).filter(Number.isFinite)
          : [];
        // An empty saved selection means "any age", which the range picker
        // shows as the full span rather than as nothing selected.
        setAgeBuckets(parsedBuckets.length ? parsedBuckets : FULL_AGE_RANGE);
        setIncludeUnknownAge(res.data.agent?.targetSource?.includeUnknownAge !== false);
        const savedPlatforms = res.data.agent?.targetSource?.platforms;
        setPlatforms(Array.isArray(savedPlatforms) ? savedPlatforms.map(String) : []);
        setIncludeUnknownPlatform(res.data.agent?.targetSource?.includeUnknownPlatform !== false);
        setSignalFilters({
          ...EMPTY_SIGNAL_FILTERS,
          ...(res.data.agent?.targetSource?.signalFilters || {})
        });
        const opt = res.data.agent?.targetSource?.autoOptimize || {};
        setAutoOptEnabled(Boolean(opt.enabled));
        setAutoOptMetric(opt.metric || 'followback');
        setAutoOptTopN(Number(opt.topN) || 50);
        setAutoOptExplorationRatio(Number(opt.explorationRatio) || 0.2);
        setAutoOptMinSampleFollows(Number(opt.minSampleFollows) || 300);
        setAutoOptCoolDownFloor(Number(opt.coolDownRateFloorPct) || 3);
        setAutoOptRefreshMinutes(Number(opt.refreshEveryMinutes) || 30);
        setAutoOptFallback(Boolean(opt.allowFallbackToUnconstrained));
        const gate = opt.scraperGating || {};
        setAutoOptGateEnabled(Boolean(gate.enabled));
        setAutoOptGatePauseAfter(Number(gate.pauseAfterFollows) || 400);
        setAutoOptGatePauseBelow(Number(gate.pauseBelowFbPct) || 1.75);
        setAutoOptGateResumeAt(Number(gate.resumeAtFbPct) || 2.0);
        setAutoOptGateRescoreHours(Number(gate.rescoreCadenceHours) || 6);
        setAutoOptGateExcludeDays(Number(gate.autoExcludeAfterDays) || 60);
        setAutoOptGateExcludeUnder(Number(gate.autoExcludeUnderPct) || 1.0);
        const life = opt.lifecycle || {};
        setLifeEnabled(Boolean(life.enabled));
        setLifeTrialSlots(Number(life.trialSlots) || 20);
        setLifeMaxInTest(Number(life.maxInTest) || 2000);
        setLifeTrialBudget(Number(life.trialBudget) || 300);
        setLifeExtensionBudget(Number.isFinite(Number(life.trialExtensionBudget)) ? Number(life.trialExtensionBudget) : 700);
        setLifeMaturityHours(Number(life.maturityHours) || 6);
        setLifePromoteAt(Number(life.promoteAtPct) || 3.0);
        setLifeParkBelow(Number.isFinite(Number(life.parkBelowPct)) ? Number(life.parkBelowPct) : 1.5);
        setLifeDemoteBelow(Number(life.demoteBelowPct) || 1.5);
        setLifeMinVerdictFollows(Number(life.minVerdictFollows) || 50);
        setLifeProvenWindow(Number(life.provenWindowDays) || 7);
        setLifeRetestDays(Number(life.parkedRetestDays) || 30);
        setLifeRescrapeBelow(Number.isFinite(Number(life.rescrapeWhenRemainingBelow)) ? Number(life.rescrapeWhenRemainingBelow) : 0);
        setLifeWilson(life.useWilsonBounds !== false);
        setAutoOptSummary({
          lastRefreshedAt: opt.lastRefreshedAt || null,
          topSources: Array.isArray(opt.topSources) ? opt.topSources : [],
          explorationSources: Array.isArray(opt.explorationSources) ? opt.explorationSources : [],
          coolDownSources: Array.isArray(opt.coolDownSources) ? opt.coolDownSources : [],
          lastScoreSummary: opt.lastScoreSummary || null,
          pausedCount: Number(gate.pausedCount) || 0,
          excludedCount: Number(gate.excludedCount) || 0,
          lastRescoredAt: gate.lastRescoredAt || null,
        });
        setFollowTestGroups(Array.isArray(res.data.agent?.followTestGroups)
          ? res.data.agent.followTestGroups
          : []);
      }
      if (!isolate) {
        try {
          const profRes = await api.get('/admin/va/ai-agents/follow-user-profiles');
          if (isCurrent(gen) && Array.isArray(profRes.data?.profiles)) {
            setUserProfiles(profRes.data.profiles);
          }
        } catch {
          /* catalog is optional on this poll */
        }
      }
    } catch (err) {
      console.error(err);
    }
  }, [selectedId, isAllAgents, bump, isCurrent, genRef]);

  // Live lifecycle counts for the auto-optimize drawer. Only polled while the
  // drawer is open — the aggregate touches every registry row for the
  // workspace and there is no reason to pay for it on the dashboard poll.
  useEffect(() => {
    if (!autoOptimizeOpen || !lifeEnabled || !selectedId || isAllAgents) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get(`/admin/va/ai-agents/${selectedId}/optimizer/lifecycle`);
        if (!cancelled) setLifeSummary(res.data || null);
      } catch (_) {
        if (!cancelled) setLifeSummary(null);
      }
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [autoOptimizeOpen, lifeEnabled, selectedId, isAllAgents]);

  const loadModels = useCallback(async () => {
    if (!selectedId || isAllAgents) {
      setModels([]);
      modelsLoadedForRef.current = '';
      return;
    }
    if (modelsLoadedForRef.current === selectedId) return;
    const agentId = selectedId;
    try {
      const res = await api.get(`/admin/va/ai-agents/${agentId}/follow-models`);
      if (agentId !== selectedId) return;
      setModels(res.data?.models || []);
      modelsLoadedForRef.current = agentId;
    } catch {
      if (agentId === selectedId) setModels([]);
    }
  }, [selectedId, isAllAgents]);

  useEffect(() => {
    liveRef.current = isLiveAgentStatus(dash?.agent?.status);
  }, [dash?.agent?.status]);

  useEffect(() => {
    if (fbfSourceModelId && followModelId && String(fbfSourceModelId) === String(followModelId)) {
      setFbfSourceModelId('');
    }
  }, [followModelId, fbfSourceModelId]);

  const followBacksFirstPayload = {
    enabled: Boolean(fbfEnabled),
    sourceModelId: fbfSourceModelId || null,
    minAgeDays: (() => {
      const n = Math.round(Number(fbfMinAgeDays));
      if (!Number.isFinite(n)) return 7;
      return Math.max(1, Math.min(90, n));
    })(),
  };

  const followBacksFirstError = () => {
    if (!fbfEnabled || sourceType !== 'scrape_v2_pool') return '';
    if (!fbfSourceModelId) {
      return 'Pick the model whose follow-backs to take, or turn follow-backs-first off';
    }
    if (String(fbfSourceModelId) === String(followModelId)) {
      return 'Follow-backs-first has to use a different model than this agent follows as';
    }
    return '';
  };

  // Per-age-band, per-platform and per-category availability for the target
  // pickers. Counts are faceted: each facet's numbers honour the other two
  // selections but ignore its own, so every option previews what picking it
  // would give you.
  useEffect(() => {
    if (isAllAgents || !selectedId || sourceType !== 'scrape_v2_pool' || !followModelId) {
      setBreakdown(null);
      return undefined;
    }
    let cancelled = false;
    const agentId = selectedId;
    // Debounced: tickboxes fire in bursts and each request is a pool count scan.
    const timer = window.setTimeout(async () => {
      setBreakdownLoading(true);
      try {
        const res = await api.get(`/admin/va/ai-agents/${agentId}/target-source/breakdown`, {
          params: {
            sourceType: 'scrape_v2_pool',
            followModelId,
            workspaceId: workspaceId || 'legacy',
            scrapeTag,
            ageBuckets: ageBuckets.join(','),
            includeUnknownAge: includeUnknownAge ? 'true' : 'false',
            platforms: platforms.join(','),
            includeUnknownPlatform: includeUnknownPlatform ? 'true' : 'false',
            signalFilters: JSON.stringify(activeSignalFilters)
          }
        });
        if (cancelled || agentId !== selectedId) return;
        if (res.data?.success) setBreakdown(res.data);
      } catch (err) {
        if (!cancelled) setBreakdown(null);
      } finally {
        if (!cancelled) setBreakdownLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    isAllAgents, selectedId, sourceType, followModelId, scrapeTag,
    ageBuckets, includeUnknownAge, platforms, includeUnknownPlatform, signalFilters, workspaceId
  ]);

  // Keep "Available to follow" in step with the pickers. The params ride along
  // on the next pool-stats poll; the debounced refresh below stops the operator
  // waiting a whole poll cycle after every change.
  useEffect(() => {
    if (isAllAgents || !selectedId || sourceType !== 'scrape_v2_pool' || !followModelId) {
      targetFilterParamsRef.current = null;
      return undefined;
    }
    targetFilterParamsRef.current = {
      followModelId,
      workspaceId: workspaceId || 'legacy',
      scrapeTag,
      ageBuckets: ageBuckets.join(','),
      includeUnknownAge: includeUnknownAge ? 'true' : 'false',
      platforms: platforms.join(','),
      includeUnknownPlatform: includeUnknownPlatform ? 'true' : 'false',
      signalFilters: JSON.stringify(activeSignalFilters)
    };
    const timer = window.setTimeout(() => {
      loadDashboard({ light: true, poolStats: true });
    }, 500);
    return () => window.clearTimeout(timer);
  }, [
    isAllAgents, selectedId, sourceType, followModelId, scrapeTag,
    ageBuckets, includeUnknownAge, platforms, includeUnknownPlatform, signalFilters, workspaceId,
    loadDashboard
  ]);

  // The picker is a range, so state is always a contiguous run of buckets.
  const ageFrom = ageBuckets.length ? Math.min(...ageBuckets) : 0;
  const ageTo = ageBuckets.length ? Math.max(...ageBuckets) : MAX_AGE_BUCKET;

  const setAgeRange = useCallback((from, to) => {
    setAgeBuckets(ageRangeToBuckets(from, to));
  }, []);

  const togglePlatform = useCallback((platform) => {
    setPlatforms((prev) => (
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform]
    ));
  }, []);

  /**
   * What the current selection would actually dispatch. The backend counts this
   * under the full filter set, so it is exact — summing the per-band counts on
   * the client would double-count rows and ignore the platform filter.
   */
  const selectedAgeTotal = breakdown?.total ?? null;

  // Instant form fields from cached agent list (Max browsers etc.) on switch.
  useEffect(() => {
    if (isAllAgents || !selectedId) {
      appliedAgentRef.current = '';
      runAccountsInitRef.current = '';
      fetchedProfilesRef.current = new Set();
      skipRunProfilesPersistRef.current = true;
      setRunAccountIds([]);
      setAccountSearch('');
      setFollowTestGroups([]);
      setUserProfiles([]);
      setSelectedProfileIds([]);
      setAccountsListExpanded(false);
      setGroupNameDraft('');
      setActiveGroupId('');
      setSelectedGroupIds([]);
      setEditDraft(null);
      setConfigureOpen(false);
      setFollowSettingsOpen(false);
      setTargetFiltersOpen(false);
      return;
    }
    if (appliedAgentRef.current === selectedId) return;
    const agent = agents.find((a) => String(a._id) === String(selectedId));
    if (!agent) return;
    appliedAgentRef.current = selectedId;
    configFromServerRef.current = '';
    setConfigureOpen(false);
    setFollowSettingsOpen(false);
    setTargetFiltersOpen(false);
    applyAgentFormFields(agent, {
      setConfig,
      setSourceType,
      setScrapeTag,
      setFollowModelId,
      // An empty saved selection means "any age"; the range picker shows that
      // as the full span rather than as an empty range.
      setAgeBuckets: (buckets) => setAgeBuckets(buckets.length ? buckets : FULL_AGE_RANGE),
      setIncludeUnknownAge,
      setPlatforms,
      setIncludeUnknownPlatform,
      setSignalFilters
    }, DEFAULT_CONFIG);
    runAccountsInitRef.current = '';
    fetchedProfilesRef.current = new Set();
    skipRunProfilesPersistRef.current = true;
    setSelectedProfileIds(loadRunProfileIds(selectedId));
    setAccountsListExpanded(false);
    setRunAccountIds([]);
    setAccountSearch('');
    setFollowTestGroups([]);
    setGroupNameDraft('');
    setActiveGroupId('');
    setSelectedGroupIds([]);
    setEditDraft(null);
    setBreakdown(null);
    setDash((prev) => (prev && String(prev.agent?._id) === String(selectedId) ? prev : null));
    pollCountRef.current = 0;
    modelsLoadedForRef.current = '';
  }, [selectedId, agents, isAllAgents]);

  useEffect(() => {
    if (isAllAgents) {
      loadOverview();
      const id = window.setInterval(loadOverview, OVERVIEW_POLL_MS);
      return () => window.clearInterval(id);
    }

    loadModels();
    let cancelled = false;
    let timer = null;

    // Cheap first paint: counters + occupancy only. Pool stats fill in
    // the background. Account rows load per ticked user-profile, not all ~2000.
    loadDashboard({ light: true, poolStats: false });
    loadDashboard({ light: true, poolStats: true, isolate: true });

    const tick = async () => {
      if (cancelled) return;
      pollCountRef.current += 1;
      const wantPool = pollCountRef.current % FULL_STATS_EVERY === 0;
      await loadDashboard({ light: true, poolStats: wantPool, isolate: true });
      if (cancelled) return;
      const ms = liveRef.current ? POLL_LIVE_MS : POLL_IDLE_MS;
      timer = window.setTimeout(tick, ms);
    };
    timer = window.setTimeout(tick, POLL_IDLE_MS);

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [isAllAgents, selectedId, loadOverview, loadDashboard, loadModels]);

  useEffect(() => {
    if (isAllAgents || !selectedId) return;
    const rows = (dash?.accounts || []).filter(isLoggedInAutomationAccount);
    const catalogIds = new Set((userProfiles || []).map((p) => String(p._id)));
    const ticked = (userProfiles || []).length
      ? selectedProfileIds.map(String).filter((id) => catalogIds.has(id))
      : selectedProfileIds.map(String);
    if (ticked.length) {
      const have = new Set(
        rows
          .filter((r) => ticked.includes(String(r.userProfileId || '')))
          .map((r) => String(r.userProfileId))
      );
      const missing = ticked.filter((id) => {
        if (have.has(id)) return false;
        const key = `${selectedId}:${id}`;
        return !fetchedProfilesRef.current.has(key);
      });
      if (missing.length) {
        for (const id of missing) fetchedProfilesRef.current.add(`${selectedId}:${id}`);
        loadAccountsForProfiles(ticked);
        return;
      }
      if (!rows.length) return;
      const allowed = new Set(rows.map((r) => String(r.accountId)));
      const { ids, runners } = accountsForUserProfiles(
        ticked,
        rows,
        userProfiles,
        allowed
      );
      setRunAccountIds(ids);
      setConfig((c) => ({
        ...c,
        accountFollowRunners: { ...(c.accountFollowRunners || {}), ...runners }
      }));
      runAccountsInitRef.current = selectedId;
      return;
    }
    if (runAccountsInitRef.current === selectedId) return;
    runAccountsInitRef.current = selectedId;
    setRunAccountIds([]);
  }, [selectedId, isAllAgents, dash?.accounts, selectedProfileIds, userProfiles, loadAccountsForProfiles]);

  useEffect(() => {
    if (!selectedId || isAllAgents) return;
    if (skipRunProfilesPersistRef.current) {
      skipRunProfilesPersistRef.current = false;
      return;
    }
    saveRunProfileIds(selectedId, selectedProfileIds);
  }, [selectedId, selectedProfileIds, isAllAgents]);

  const runIdSet = useMemo(() => new Set(runAccountIds.map(String)), [runAccountIds]);
  const searchTokens = useMemo(() => parseUsernameQuery(accountSearch), [accountSearch]);
  const loggedInAccounts = useMemo(
    () => (dash?.accounts || []).filter(isLoggedInAutomationAccount),
    [dash?.accounts]
  );
  const loggedInIdSet = useMemo(
    () => new Set(loggedInAccounts.map((r) => String(r.accountId))),
    [loggedInAccounts]
  );
  const accountById = useMemo(() => {
    const map = new Map();
    for (const row of dash?.accounts || []) map.set(String(row.accountId), row);
    for (const row of dash?.parkedAccounts || []) map.set(String(row.accountId), row);
    return map;
  }, [dash?.accounts, dash?.parkedAccounts]);
  const visibleAccounts = useMemo(() => {
    if (!searchTokens.length) return loggedInAccounts;
    return loggedInAccounts.filter((row) => {
      const name = String(row.username || '').toLowerCase();
      return searchTokens.some((token) => name.includes(token));
    });
  }, [loggedInAccounts, searchTokens]);
  const visibleIds = useMemo(
    () => visibleAccounts.map((r) => String(r.accountId)),
    [visibleAccounts]
  );
  const occupancy = useMemo(() => {
    if (dash?.occupancy && typeof dash.occupancy.loggedIn === 'number') {
      return {
        loggedIn: Number(dash.occupancy.loggedIn) || 0,
        attached: Number(dash.occupancy.attached) || 0,
        available: Number(dash.occupancy.available) || 0
      };
    }
    let attached = 0;
    for (const row of loggedInAccounts) {
      if (row.userProfileId) attached += 1;
    }
    const loggedIn = loggedInAccounts.length;
    return {
      loggedIn,
      attached,
      available: Math.max(0, loggedIn - attached)
    };
  }, [dash?.occupancy, loggedInAccounts]);
  const occupancyByProfile = useMemo(() => {
    const nById = new Map();
    if (Array.isArray(dash?.occupancy?.byProfile) && dash.occupancy.byProfile.length) {
      for (const slot of dash.occupancy.byProfile) {
        if (!slot?.profileId) continue;
        nById.set(String(slot.profileId), Number(slot.count) || 0);
      }
      return nById;
    }
    for (const row of loggedInAccounts) {
      const pid = row.userProfileId ? String(row.userProfileId) : '';
      if (!pid) continue;
      nById.set(pid, (nById.get(pid) || 0) + 1);
    }
    return nById;
  }, [dash?.occupancy, loggedInAccounts]);
  const liveRunGroups = useMemo(() => {
    const agentStatus = dash?.agent?.status || 'idle';
    const live = ['running', 'pausing', 'paused'].includes(agentStatus);
    if (!live) return [];
    const catalog = new Map((userProfiles || []).map((p) => [String(p._id), p]));
    const emptyGroup = (pid) => {
      const meta = pid ? catalog.get(pid) : null;
      return {
        profileId: pid || null,
        name: meta?.name || (pid ? 'User-profile' : 'No user-profile'),
        runner: meta ? labelFollowRunner(meta.followRunner) : '—',
        accounts: [],
        atCap: 0,
        sending: 0,
        followed: 0,
        goalTotal: 0
      };
    };
    const by = new Map();
    for (const slot of dash?.dailyGoal?.byProfile || []) {
      const pid = slot.profileId ? String(slot.profileId) : '';
      const g = emptyGroup(pid);
      g.atCap = Number(slot.atCap) || 0;
      g.goalTotal = Number(slot.total) || 0;
      by.set(pid, g);
    }
    const rows = loggedInAccounts.filter(isLiveJobRosterRow);
    for (const row of rows) {
      const pid = row.userProfileId ? String(row.userProfileId) : '';
      if (!by.has(pid)) by.set(pid, emptyGroup(pid));
      const g = by.get(pid);
      g.accounts.push(row);
      g.followed += Number(row.followed) || 0;
      if (row.jobStatus === 'running' || row.jobStatus === 'warmup') g.sending += 1;
      if (
        !g.goalTotal
        && (
          row.jobStatus === 'daily_limit'
          || (Number(row.budgetTotal) > 0 && Number(row.budgetRemaining) <= 0)
        )
      ) g.atCap += 1;
    }
    for (const g of by.values()) {
      if (!g.goalTotal) g.goalTotal = g.accounts.length;
      g.accounts.sort((a, b) => {
        const send = (s) => (s === 'running' || s === 'warmup' ? 0 : 1);
        const d = send(a.jobStatus) - send(b.jobStatus);
        if (d) return d;
        return String(a.username || '').localeCompare(String(b.username || ''));
      });
    }
    return [...by.values()].sort((a, b) => (b.goalTotal || b.accounts.length) - (a.goalTotal || a.accounts.length));
  }, [loggedInAccounts, userProfiles, dash?.agent?.status, dash?.dailyGoal?.byProfile]);
  const liveRosterCount = useMemo(
    () => liveRunGroups.reduce((n, g) => n + (g.goalTotal || g.accounts.length || 0), 0),
    [liveRunGroups]
  );
  const visibleLiveRunGroups = useMemo(() => {
    if (!searchTokens.length) return liveRunGroups;
    return liveRunGroups
      .map((g) => ({
        ...g,
        visibleAccounts: g.accounts.filter((row) => {
          const name = String(row.username || '').toLowerCase();
          return searchTokens.some((token) => name.includes(token));
        })
      }))
      .filter((g) => g.visibleAccounts.length > 0);
  }, [liveRunGroups, searchTokens]);
  const buildCounts = useMemo(
    () => countRunBuildsFromProfiles(runAccountIds, accountById, userProfiles),
    [runAccountIds, accountById, userProfiles]
  );
  const selectedAgent = useMemo(
    () => agents.find((a) => String(a._id) === String(selectedId)),
    [agents, selectedId]
  );
  const orbexAvailable = Boolean(
    selectedAgent?.runtime?.orbexInstalled || dash?.agent?.orbexInstalled
  );
  const browserEngine = String(config.browserEngine || '').toLowerCase() === 'orbex'
    ? 'orbex'
    : 'gologin';
  const startReadyCount = useMemo(() => {
    if (browserEngine !== 'orbex') return buildCounts.total;
    return runAccountIds.filter((id) => {
      const row = accountById.get(String(id));
      return Boolean(String(row?.orbexProfileId || '').trim());
    }).length;
  }, [browserEngine, buildCounts.total, runAccountIds, accountById]);
  const addableRunIds = useMemo(
    () => runAccountIds.filter((id) => {
      const row = accountById.get(String(id));
      if (!row) return false;
      if (row.inJob === true) return false;
      if (row.inJob === false) return true;
      return !(Number(row.budgetTotal) > 0);
    }),
    [runAccountIds, accountById]
  );
  const addableBuildCounts = useMemo(
    () => countRunBuildsFromProfiles(addableRunIds, accountById, userProfiles),
    [addableRunIds, accountById, userProfiles]
  );
  const rankedUserProfiles = useMemo(() => {
    return [...userProfiles].sort((a, b) => {
      const aOn = (occupancyByProfile.get(String(a._id)) || 0) > 0 ? 0 : 1;
      const bOn = (occupancyByProfile.get(String(b._id)) || 0) > 0 ? 0 : 1;
      return aOn - bOn;
    });
  }, [userProfiles, occupancyByProfile]);

  const minutesFromMs = (ms) => Math.round((Number(ms) || 0) / 60_000);
  const followModelLabel = useMemo(() => {
    const m = models.find((x) => String(x._id) === String(followModelId));
    return m ? (m.displayName || m.name) : 'No model';
  }, [models, followModelId]);
  const followSettingsSummary = [
    followModelLabel,
    SOURCE_TYPE_LABELS[sourceType] || sourceType,
    sourceType === 'paste' ? null : (scrapeTag || 'All categories'),
    `${config.maxConcurrentBrowsers} browsers`,
    `${config.followsPerAccountMin}–${config.followsPerAccountMax} · ${config.totalFollowsPerProfileMin}–${config.totalFollowsPerProfileMax}/day`,
    `wait ${minutesFromMs(config.roundGapMinMs)}–${minutesFromMs(config.roundGapMaxMs)}m`
  ].filter(Boolean).join(' · ');
  const targetFiltersSummary = !followModelId
    ? 'Select a follow model'
    : breakdownLoading && !breakdown
      ? 'Counting…'
      : selectedAgeTotal != null
        ? `${Number(selectedAgeTotal).toLocaleString()} ready`
        : '—';
  const setGapMinutes = (key, minutes) => {
    const n = Math.max(0, Number(minutes) || 0);
    setConfig((c) => {
      const next = { ...c, [key]: n * 60_000 };
      if (key === 'roundGapMinMs' && next.roundGapMaxMs < next.roundGapMinMs) {
        next.roundGapMaxMs = next.roundGapMinMs;
      }
      if (key === 'roundGapMaxMs' && next.roundGapMaxMs < next.roundGapMinMs) {
        next.roundGapMinMs = next.roundGapMaxMs;
      }
      return next;
    });
  };

  const toggleRunAccount = (id) => {
    const s = String(id);
    setRunAccountIds((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const selectAllRunAccounts = () => {
    setRunAccountIds(loggedInAccounts.map((r) => String(r.accountId)));
  };

  const selectAccountIds = (ids, on) => {
    const nextIds = ids.map(String);
    setRunAccountIds((prev) => {
      if (on) return [...new Set([...prev.map(String), ...nextIds])];
      const drop = new Set(nextIds);
      return prev.map(String).filter((id) => !drop.has(id));
    });
  };

  const selectVisibleAccounts = (on) => {
    selectAccountIds(visibleIds, on);
  };

  const shrinkAccountList = () => {
    setAccountsListExpanded(false);
    window.requestAnimationFrame(() => {
      accountsSectionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  };

  const assignBuildTo = (runner, ids) => {
    const v = normalizeFollowRunner(runner);
    setConfig((c) => {
      const map = { ...(c.accountFollowRunners || {}) };
      for (const id of ids) map[String(id)] = v;
      return { ...c, accountFollowRunners: map };
    });
  };

  const snapshotCurrentGroup = (name, id) => {
    const allowed = loggedInIdSet;
    const accountIds = runAccountIds.map(String).filter((accId) => allowed.has(accId));
    const accountFollowRunners = {};
    for (const accId of accountIds) {
      accountFollowRunners[accId] = normalizeFollowRunner(config.accountFollowRunners?.[accId]);
    }
    return {
      id: id || newGroupId(),
      name,
      accountIds,
      accountFollowRunners,
      config: pickGroupConfig(config),
      updatedAt: new Date().toISOString()
    };
  };

  const persistFollowTestGroups = async (next) => {
    // Deprecated: Automations no longer writes VPS-local test groups.
    setFollowTestGroups(next);
  };

  const applySelectedGroups = (groupIds, groupsOverride) => {
    const idSet = new Set((groupIds || []).map(String));
    const groups = (groupsOverride || followTestGroups).filter((g) => idSet.has(String(g.id)));
    const { ids, accountFollowRunners } = mergeGroupRunSelection(groups, loggedInIdSet);
    const firstConfig = groups[0] ? pickGroupConfig(groups[0].config) : {};
    const sameKnobs = groups.length > 0 && groups.every((g) => {
      const cfg = pickGroupConfig(g.config);
      return FOLLOW_TEST_GROUP_CONFIG_KEYS.every(
        (key) => Number(cfg[key] || 0) === Number(firstConfig[key] || 0)
      );
    });
    setRunAccountIds(ids);
    setConfig((c) => ({
      ...c,
      ...(sameKnobs ? firstConfig : {}),
      accountFollowRunners: {
        ...(c.accountFollowRunners || {}),
        ...accountFollowRunners
      }
    }));
    setShowAccounts(true);
    return ids;
  };

  const applySelectedProfiles = (profileIds) => {
    const { ids, runners } = accountsForUserProfiles(
      profileIds,
      loggedInAccounts,
      userProfiles,
      loggedInIdSet
    );
    setRunAccountIds(ids);
    setConfig((c) => ({
      ...c,
      accountFollowRunners: { ...(c.accountFollowRunners || {}), ...runners }
    }));
    return ids;
  };

  const toggleProfileSelected = (profileId) => {
    const id = String(profileId);
    const next = selectedProfileIds.includes(id)
      ? selectedProfileIds.filter((x) => x !== id)
      : [...selectedProfileIds, id];
    setSelectedProfileIds(next);
    if (next.length) applySelectedProfiles(next);
    else setRunAccountIds([]);
  };

  const handleAutoAssign = async ({
    profileId = null,
    profileIds = null,
    amount: amountArg,
    profileName = '',
    allUnattached = false
  } = {}) => {
    if (!selectedId || isAllAgents) return;
    const amount = allUnattached
      ? 'all'
      : Math.max(1, Number(amountArg != null ? amountArg : autoAssignAmount) || 0);
    setAssignBusy(true);
    try {
      const res = await api.post(`/admin/va/ai-agents/${selectedId}/follow-user-profiles/auto-assign`, {
        amount,
        ...(profileId ? { profileId } : {}),
        ...(Array.isArray(profileIds) && profileIds.length ? { profileIds } : {})
      });
      const n = Number(res.data?.assigned) || 0;
      const short = Number(res.data?.shortfall) || 0;
      const where = profileName ? ` → ${profileName}` : '';
      toast.success(
        short
          ? `Assigned ${n}${where} · ${short} short of requested`
          : `Assigned ${n} logged-in account(s)${where}`
      );
      await loadDashboard({ light: false });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Auto-assign failed');
    } finally {
      setAssignBusy(false);
    }
  };

  const handleUnassignProfiles = async ({
    profileId = null,
    profileIds = null,
    profileName = '',
    count = 0
  } = {}) => {
    if (!selectedId || isAllAgents) return;
    const where = profileName
      ? ` from ${profileName}`
      : profileIds?.length
        ? ` from ${profileIds.length} selected profile(s)`
        : ' from every user-profile on this VPS';
    const n = Number(count) || 0;
    const ok = window.confirm(
      n
        ? `Unassign ${n} account(s) on this VPS${where}? They stay on the agent — only the user-profile sticker is removed.`
        : `Unassign user-profiles on this VPS${where}? Accounts stay on the agent.`
    );
    if (!ok) return;
    setAssignBusy(true);
    try {
      const res = await api.post(`/admin/va/ai-agents/${selectedId}/follow-user-profiles/unassign`, {
        ...(profileId ? { profileId } : {}),
        ...(Array.isArray(profileIds) && profileIds.length ? { profileIds } : {})
      });
      const removed = Number(res.data?.unassigned) || 0;
      toast.success(`Removed ${removed} user-profile assignment(s)`);
      await loadDashboard({ light: false });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Remove failed');
    } finally {
      setAssignBusy(false);
    }
  };

  const handleSaveGroup = async () => {
    const name = groupNameDraft.trim();
    if (!name) {
      toast.error('Name the group first');
      return;
    }
    if (!runAccountIds.length) {
      toast.error('Tick at least one account before saving a group');
      return;
    }
    const existing = followTestGroups.find(
      (g) => String(g.name).toLowerCase() === name.toLowerCase()
    );
    const nextGroup = snapshotCurrentGroup(name, existing?.id);
    const next = existing
      ? followTestGroups.map((g) => (g.id === existing.id ? nextGroup : g))
      : [...followTestGroups, nextGroup];
    try {
      await persistFollowTestGroups(next);
      setActiveGroupId(nextGroup.id);
      setGroupNameDraft('');
      toast.success(existing ? `Updated ${name}` : `Saved ${name}`);
    } catch {
      /* persistFollowTestGroups already toasted */
    }
  };

  const handleSaveGroupEdit = async () => {
    if (!editDraft) return;
    const name = String(editDraft.name || '').trim();
    if (!name) {
      toast.error('Name the group first');
      return;
    }
    const nextGroup = {
      id: editDraft.id,
      name,
      accountIds: editDraft.rows.map((r) => String(r.accountId)),
      accountFollowRunners: Object.fromEntries(
        editDraft.rows.map((r) => [
          String(r.accountId),
          normalizeFollowRunner(r.runner)
        ])
      ),
      config: pickGroupConfig(editDraft.config),
      updatedAt: new Date().toISOString()
    };
    const next = followTestGroups.map((g) => (g.id === editDraft.id ? nextGroup : g));
    try {
      await persistFollowTestGroups(next);
      setActiveGroupId(nextGroup.id);
      setEditDraft(null);
      if (selectedGroupIds.includes(String(nextGroup.id))) {
        applySelectedGroups(selectedGroupIds, next);
      }
      toast.success(`Saved ${name}`);
    } catch {
      /* already toasted */
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Delete group “${group.name}”? Accounts stay on the VPS.`)) return;
    const next = followTestGroups.filter((g) => g.id !== group.id);
    try {
      await persistFollowTestGroups(next);
      if (activeGroupId === group.id) setActiveGroupId('');
      if (editDraft?.id === group.id) setEditDraft(null);
      const nextSelected = selectedGroupIds.filter((id) => id !== String(group.id));
      setSelectedGroupIds(nextSelected);
      if (nextSelected.length) applySelectedGroups(nextSelected);
      toast.success(`Deleted ${group.name}`);
    } catch {
      /* already toasted */
    }
  };

  const handleStart = async (opts = {}) => {
    if (!selectedId || isAllAgents) return;
    const allowed = loggedInIdSet;
    let selected = (opts.accountIds || runAccountIds).map(String).filter((id) => allowed.has(id));
    if (!selected.length) {
      toast.error('Tick a user profile or at least one account');
      setShowAccounts(true);
      return;
    }
    const fbfError = followBacksFirstError();
    if (fbfError) {
      toast.error(fbfError);
      return;
    }
    const startEngine = String((opts.configPatch?.browserEngine || config.browserEngine) || '').toLowerCase() === 'orbex'
      ? 'orbex'
      : 'gologin';
    if (startEngine === 'orbex') {
      if (!orbexAvailable) {
        toast.error('Orbex is not installed on this VPS');
        return;
      }
      const ready = selected.filter((id) => String(accountById.get(id)?.orbexProfileId || '').trim());
      if (!ready.length) {
        toast.error('None of the selected accounts have an Orbex profile — import them first');
        setShowAccounts(true);
        return;
      }
      if (ready.length < selected.length) {
        toast.success(`${selected.length - ready.length} account(s) skipped — no Orbex profile`);
      }
      selected = ready;
    }
    const mergedConfig = { ...config, ...(opts.configPatch || {}), browserEngine: startEngine };
    const runnerSource = opts.accountFollowRunners || mergedConfig.accountFollowRunners || {};
    const accountFollowRunners = {};
    for (const id of selected) {
      accountFollowRunners[id] = normalizeFollowRunner(runnerSource[id]);
    }
    const allHumanized = selected.every((id) => isHumanizedFollowRunner(accountFollowRunners[id]));
    setBusy(true);
    try {
      // Single round-trip — /start accepts config, source, model, modules, autoContinuation.
      const res = await api.post(`/admin/va/ai-agents/${selectedId}/start`, {
        useTargetPool: sourceType === 'paste',
        targets: [],
        config: {
          ...mergedConfig,
          warmupEnabled: true,
          followRunner: allHumanized ? FOLLOW_RUNNER_V3 : FOLLOW_RUNNER_CLASSIC,
          accountFollowRunners
        },
        targetSource: {
          type: sourceType,
          workspaceId: workspaceId || 'legacy',
          scrapeTag: scrapeTag || null,
          bufferDays: 1,
          ageBuckets,
          includeUnknownAge,
          platforms,
          includeUnknownPlatform,
          signalFilters: activeSignalFilters,
          autoOptimize: {
            enabled: autoOptEnabled,
            metric: autoOptMetric,
            topN: autoOptTopN,
            explorationRatio: autoOptExplorationRatio,
            minSampleFollows: autoOptMinSampleFollows,
            coolDownRateFloorPct: autoOptCoolDownFloor,
            refreshEveryMinutes: autoOptRefreshMinutes,
            allowFallbackToUnconstrained: autoOptFallback,
            scraperGating: {
              enabled: autoOptGateEnabled,
              pauseAfterFollows: autoOptGatePauseAfter,
              pauseBelowFbPct: autoOptGatePauseBelow,
              resumeAtFbPct: autoOptGateResumeAt,
              rescoreCadenceHours: autoOptGateRescoreHours,
              autoExcludeAfterDays: autoOptGateExcludeDays,
              autoExcludeUnderPct: autoOptGateExcludeUnder,
            },
            lifecycle: buildLifecyclePayload(),
          },
          ...(sourceType === 'scrape_v2_pool' ? { followBacksFirst: followBacksFirstPayload } : {}),
        },
        followModelId: followModelId || undefined,
        autoContinuation: { enabled: true },
        automationType: 'follow',
        modules: CLASSIC_MODULES,
        excludeColdAccounts: excludeCold,
        onlyAccountIds: selected
      });
      const coldDeselected = Number(res.data?.coldDeselected) || 0;
      toast.success(
        res.data?.preparing
          ? 'Preparing targets…'
          : res.data?.job?.totals?.totalTargets != null
            ? `Started (${Number(res.data.job.totals.totalTargets).toLocaleString()} targets)`
            : 'Automation started'
      );
      if (coldDeselected) {
        toast.success(`${coldDeselected.toLocaleString()} cold account(s) left out of this run`);
      }
      await Promise.all([
        loadDashboard({ light: false }),
        reloadAgents({ silent: true, force: true })
      ]);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start');
    } finally {
      setBusy(false);
    }
  };

  const handleAddToJob = async () => {
    if (!selectedId || isAllAgents) return;
    const selected = addableRunIds.map(String);
    if (!selected.length) {
      toast.error('Tick a user-profile that is not already in this job');
      return;
    }
    const accountFollowRunners = {};
    for (const id of selected) {
      accountFollowRunners[id] = normalizeFollowRunner(config.accountFollowRunners?.[id]);
    }
    setBusy(true);
    try {
      const res = await api.post(`/admin/va/ai-agents/${selectedId}/live-run/add-accounts`, {
        accountIds: selected,
        accountFollowRunners
      });
      const n = Number(res.data?.added) || (res.data?.addedAccountIds || []).length;
      toast.success(
        n
          ? `Added ${n.toLocaleString()} account(s) to this job — they join on the next round`
          : 'Those accounts are already in this job'
      );
      await Promise.all([
        loadDashboard({ light: false }),
        reloadAgents({ silent: true, force: true })
      ]);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add accounts to this job');
    } finally {
      setBusy(false);
    }
  };

  const handleTestProfile = (profile) => {
    const allowed = loggedInIdSet;
    const { ids, runners } = accountsForUserProfiles(
      [profile._id],
      loggedInAccounts,
      userProfiles,
      allowed
    );
    if (!ids.length) {
      toast.error('No logged-in accounts on this VPS for that profile');
      return;
    }
    handleStart({ accountIds: ids, accountFollowRunners: runners });
  };

  /**
   * Pull cold accounts out of the rotation now. This ejects rather than merely
   * deselecting, so an account already holding a budget in the live job stops
   * on the worker's next run-state poll instead of finishing its round.
   */
  const handleStopColdAccounts = async () => {
    const scope = isAllAgents ? 'every VPS' : 'this VPS';
    if (!window.confirm(
      `Stop every account on ${scope} that has sent 100 follows without a follow-back?`
    )) return;
    setColdBusy(true);
    try {
      const res = isAllAgents
        ? await api.post('/admin/va/ai-agents/cold-accounts/deselect-all', {})
        : await api.post(`/admin/va/ai-agents/${selectedId}/cold-accounts/deselect`, {});
      const stopped = Number(res.data?.stopped) || 0;
      toast.success(
        stopped
          ? `Stopped ${stopped.toLocaleString()} cold account(s)`
          : 'No cold accounts — every account has had a recent follow-back'
      );
      await Promise.all([
        isAllAgents ? loadOverview() : loadDashboard({ light: false }),
        reloadAgents({ silent: true, force: true })
      ]);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to stop cold accounts');
    } finally {
      setColdBusy(false);
    }
  };

  /**
   * Persist the target filters without restarting. Mid-job top-ups re-read the
   * agent, so a live job picks these up on its next batch.
   */
  const handleApplyTargetFilters = async () => {
    if (!selectedId || isAllAgents) return;
    const fbfError = followBacksFirstError();
    if (fbfError) {
      toast.error(fbfError);
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/va/ai-agents/${selectedId}/target-source`, {
        workspaceId: workspaceId || 'legacy',
        scrapeTag: scrapeTag || null,
        ageBuckets,
        includeUnknownAge,
        platforms,
        includeUnknownPlatform,
        signalFilters: activeSignalFilters,
        autoOptimize: {
          enabled: autoOptEnabled,
          metric: autoOptMetric,
          topN: autoOptTopN,
          explorationRatio: autoOptExplorationRatio,
          minSampleFollows: autoOptMinSampleFollows,
          coolDownRateFloorPct: autoOptCoolDownFloor,
          refreshEveryMinutes: autoOptRefreshMinutes,
          allowFallbackToUnconstrained: autoOptFallback,
          scraperGating: {
            enabled: autoOptGateEnabled,
            pauseAfterFollows: autoOptGatePauseAfter,
            pauseBelowFbPct: autoOptGatePauseBelow,
            resumeAtFbPct: autoOptGateResumeAt,
            rescoreCadenceHours: autoOptGateRescoreHours,
            autoExcludeAfterDays: autoOptGateExcludeDays,
            autoExcludeUnderPct: autoOptGateExcludeUnder,
          },
          lifecycle: buildLifecyclePayload(),
        },
        ...(sourceType === 'scrape_v2_pool' ? { followBacksFirst: followBacksFirstPayload } : {}),
      });
      toast.success('Target filters applied — next top-up will use them');
      await loadDashboard({ light: true, poolStats: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to apply filters');
    } finally {
      setBusy(false);
    }
  };

  const handleStop = async (agentId = selectedId) => {
    if (!agentId || agentId === 'all') return;
    setBusy(true);
    try {
      await api.post(`/admin/va/ai-agents/${agentId}/stop`, {});
      toast.success('Stop requested');
      if (isAllAgents) await loadOverview();
      else await Promise.all([loadDashboard({ light: false }), reloadAgents({ silent: true, force: true })]);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to stop');
    } finally {
      setBusy(false);
    }
  };

  const handlePause = async (agentId = selectedId) => {
    if (!agentId || agentId === 'all') return;
    setBusy(true);
    try {
      await api.post(`/admin/va/ai-agents/${agentId}/pause`, {});
      toast.success('Pause requested');
      if (isAllAgents) await loadOverview();
      else await loadDashboard({ light: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to pause');
    } finally {
      setBusy(false);
    }
  };

  const handleResume = async (agentId = selectedId) => {
    if (!agentId || agentId === 'all') return;
    setBusy(true);
    try {
      await api.post(`/admin/va/ai-agents/${agentId}/resume`, {});
      toast.success('Resumed');
      if (isAllAgents) await loadOverview();
      else await loadDashboard({ light: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to resume');
    } finally {
      setBusy(false);
    }
  };

  if (user?.role !== 'admin') {
    return <div className="text-sm text-gray-500">Admin access required.</div>;
  }

  const agentStatus = dash?.agent?.status || 'idle';
  const running = ['running', 'pausing'].includes(agentStatus);
  const paused = agentStatus === 'paused';
  const liveAccountCount = visibleLiveRunGroups.reduce(
    (n, group) => n + (group.visibleAccounts || group.accounts).length,
    0
  );
  const accountListCount = (running || paused) ? liveAccountCount : visibleAccounts.length;
  const accountListCanFold = accountListCount > ACCOUNT_LIST_PREVIEW;
  const accountListFolded = accountListCanFold && !accountsListExpanded;
  let shownAccounts = visibleAccounts;
  let shownLiveGroups = visibleLiveRunGroups;
  if (accountListFolded) {
    if (running || paused) {
      let left = ACCOUNT_LIST_PREVIEW;
      shownLiveGroups = [];
      for (const group of visibleLiveRunGroups) {
        if (left <= 0) break;
        const rows = group.visibleAccounts || group.accounts;
        const slice = rows.slice(0, left);
        left -= slice.length;
        if (slice.length) shownLiveGroups.push({ ...group, visibleAccounts: slice });
      }
    } else {
      shownAccounts = visibleAccounts.slice(0, ACCOUNT_LIST_PREVIEW);
    }
  }
  const shownIds = shownAccounts.map((row) => String(row.accountId));
  const allShownSelected = shownIds.length > 0 && shownIds.every((id) => runIdSet.has(id));
  const overall = overview?.overall || {};
  const overviewRows = overview?.agents || [];
  const setupAgent = dash?.agent
    || agents.find((a) => String(a._id) === String(selectedId))
    || null;

  return (
    <div className="space-y-6">
      <VpsPageHeader
        title="VPS Automations"
        subtitle="URL follow: own profile + Home scroll, then follows on the VPS — same ledger as followback scanners."
        actions={(
          <>
            <VpsAgentPicker
              agents={agents}
              selectedId={selectedId}
              onSelect={selectAgent}
              allowAll
              onChanged={async (id) => {
                await reloadAgents({ force: true, silent: true });
                if (id) selectAgent(id);
              }}
            />
            {isAllAgents && (
              <button
                type="button"
                disabled={coldBusy}
                onClick={handleStopColdAccounts}
                title="Across every VPS, ejects accounts that have sent 100 follows since their last follow-back."
                className="btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1"
              >
                <NoSymbolIcon className="w-4 h-4" />
                {coldBusy ? 'Stopping…' : 'Stop cold accounts'}
              </button>
            )}
            <button
              type="button"
              onClick={() => (isAllAgents ? loadOverview() : loadDashboard({ light: false }))}
              className="btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1"
            >
              <ArrowPathIcon className="w-4 h-4" /> Refresh
            </button>
          </>
        )}
      />

      {/* Auto-optimize runtime control + monitor. Always visible; internal
          poll drives its own updates independent of the agent selector. */}
      <AutoOptimizeControlPanel />

      {agentsLoading && !agents.length ? (
        <div className="text-sm text-gray-500">Loading agents…</div>
      ) : !agents.length ? (
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 p-8 text-center text-sm text-gray-500">
          No AI VA agents yet. Create one under{' '}
          <Link className="text-indigo-600 dark:text-indigo-400 underline" to="/dashboard/admin/vas">
            Virtual Assistants → AI VA
          </Link>
          .
        </div>
      ) : isAllAgents ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            {[
              { label: 'Agents', value: fmt(overall.agents) },
              { label: 'Desktop online', value: fmt(overall.online) },
              { label: 'Running', value: fmt(overall.running) },
              { label: 'Follows (live jobs)', value: fmt(overall.followsSent) },
              { label: 'Active accounts', value: fmt(overall.enabledAccounts) },
              { label: 'At daily cap', value: fmt(overall.atCap) }
            ].map((c) => (
              <div
                key={c.label}
                className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3"
              >
                <div className="text-[11px] uppercase tracking-wide text-gray-500">{c.label}</div>
                <div className="mt-1 text-xl font-semibold tabular-nums text-gray-900 dark:text-white">
                  {c.value}
                </div>
              </div>
            ))}
          </div>

          <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
            <p className="text-xs text-gray-500">
              Select an agent row to configure, start, or inspect per-account progress. Captcha and auth-expired stay on the VPS but are hidden from this page until the profile is logged in again.
            </p>
          </section>

          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Agent</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-left font-medium">Desktop</th>
                  <th className="px-3 py-2 text-right font-medium">Accounts</th>
                  <th className="px-3 py-2 text-right font-medium">Follows</th>
                  <th className="px-3 py-2 text-right font-medium">Daily cap</th>
                  <th className="px-3 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {overviewRows.map((row) => {
                  const rowRunning = ['running', 'pausing', 'warmup'].includes(row.status);
                  const rowPaused = row.status === 'paused';
                  const dailyTotal = row.dailyGoal?.total || 0;
                  const dailyAtCap = row.dailyGoal?.atCap || 0;
                  return (
                    <tr key={row.agentId} className="hover:bg-gray-50/80 dark:hover:bg-gray-800/40">
                      <td className="px-3 py-2.5 font-medium">
                        <button
                          type="button"
                          className="text-indigo-600 dark:text-indigo-400 hover:underline text-left"
                          onClick={() => selectAgent(row.agentId)}
                        >
                          {row.name}
                        </button>
                        <div className="text-[11px] text-gray-400 font-normal">
                          {row.targetSourceType}
                          {row.scrapeTag ? ` · ${row.scrapeTag}` : ''}
                          {row.maxBrowsers != null ? ` · ${row.maxBrowsers} browsers` : ''}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${statusTone(row.status)}`}>
                          {row.status}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-xs">
                        {row.online ? (
                          <span className="text-emerald-600 dark:text-emerald-400">online</span>
                        ) : (
                          <span className="text-gray-400">offline</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {fmt(row.enabledAccountCount)}
                        <span className="text-gray-400 text-xs"> / {fmt(row.accountCount)}</span>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmt(row.followsSent)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {fmt(dailyAtCap)}
                        <span className="text-gray-400 text-xs"> / {fmt(dailyTotal)}</span>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="inline-flex items-center gap-1">
                          {rowPaused && (
                            <button
                              type="button"
                              disabled={busy}
                              title="Resume"
                              onClick={() => handleResume(row.agentId)}
                              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-emerald-600"
                            >
                              <PlayIcon className="w-4 h-4" />
                            </button>
                          )}
                          {rowRunning && (
                            <button
                              type="button"
                              disabled={busy}
                              title="Pause"
                              onClick={() => handlePause(row.agentId)}
                              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-violet-600"
                            >
                              <PauseIcon className="w-4 h-4" />
                            </button>
                          )}
                          {(rowRunning || rowPaused) && (
                            <button
                              type="button"
                              disabled={busy}
                              title="Stop"
                              onClick={() => handleStop(row.agentId)}
                              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-red-600"
                            >
                              <StopIcon className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline px-1"
                            onClick={() => selectAgent(row.agentId)}
                          >
                            Open
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!overviewRows.length && (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-gray-500">
                      No agents yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          {/* Controls */}
          <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusTone(agentStatus)}`}>
                  {agentStatus}
                </span>
                {dash?.agent?.stalled && (
                  <span
                    className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                    title="Desktop heartbeat is healthy, but the job itself has reported no progress in a while — likely a hung browser launch on the VPS. Usually needs the desktop-agent process restarted."
                  >
                    Stalled{dash.agent.stalledForMinutes != null ? ` · ${dash.agent.stalledForMinutes}min` : ''}
                  </span>
                )}
                <span className="text-sm text-gray-500">
                  {dash?.agent?.online ? 'Desktop online' : 'Desktop offline'}
                  {dash?.agent?.enabledAccountCount != null && (
                    <> · {fmt(dash.agent.enabledAccountCount)} active accounts</>
                  )}
                </span>
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <div className="text-xs space-y-1">
                  <span className="text-gray-500 uppercase tracking-wide">
                    {running || paused ? 'Add to this job' : 'This run'}
                  </span>
                  <div
                    className="text-sm min-w-[14rem] px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60"
                    title={running || paused
                      ? 'Tick user-profiles that are not already in this job, then Add.'
                      : 'Tick user profiles to include those accounts. Unticked accounts stay attached but sit this job out. Refresh keeps the ticked profiles for this VPS.'}
                  >
                    {running || paused
                      ? (addableBuildCounts.total
                        ? `${addableBuildCounts.total} not in this job · ${formatFollowBuildCounts(addableBuildCounts)}`
                        : 'Tick a user-profile that is not already running')
                      : (buildCounts.total
                        ? `${buildCounts.total} accounts · ${formatFollowBuildCounts(buildCounts)}`
                        : 'Tick user profiles or accounts')}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setConfigureOpen((v) => !v)}
                  className="btn-secondary !py-2 !px-4 text-sm inline-flex items-center gap-1.5"
                >
                  <Cog6ToothIcon className="w-4 h-4" />
                  Configure agent
                  {configureOpen
                    ? <ChevronUpIcon className="w-4 h-4" />
                    : <ChevronDownIcon className="w-4 h-4" />}
                </button>
                {!running && !paused && (
                  <>
                    <div className="text-xs space-y-1">
                      <span className="text-gray-500 uppercase tracking-wide">Browser</span>
                      <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setConfig((c) => ({ ...c, browserEngine: 'gologin' }))}
                          className={`px-3 py-2 text-sm ${browserEngine === 'gologin'
                            ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200'}`}
                        >
                          GoLogin
                        </button>
                        <button
                          type="button"
                          disabled={!orbexAvailable}
                          title={orbexAvailable ? 'Launch follow in Orbex' : 'Orbex is not installed on this VPS'}
                          onClick={() => setConfig((c) => ({ ...c, browserEngine: 'orbex' }))}
                          className={`px-3 py-2 text-sm border-l border-gray-200 dark:border-gray-700 ${
                            !orbexAvailable
                              ? 'opacity-40 cursor-not-allowed bg-gray-50 dark:bg-gray-900 text-gray-400'
                              : browserEngine === 'orbex'
                                ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                          }`}
                        >
                          Orbex
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={busy || !startReadyCount}
                      onClick={() => handleStart()}
                      className="btn-primary !py-2 !px-4 text-sm inline-flex items-center gap-1.5"
                    >
                      <PlayIcon className="w-4 h-4" />
                      {startReadyCount
                        ? `Start follow (${startReadyCount})`
                        : (browserEngine === 'orbex'
                          ? 'Import Orbex first'
                          : 'Start follow')}
                    </button>
                  </>
                )}
                {(running || paused) && (
                  <button
                    type="button"
                    disabled={busy || !addableBuildCounts.total}
                    onClick={() => handleAddToJob()}
                    className="btn-primary !py-2 !px-4 text-sm inline-flex items-center gap-1.5"
                  >
                    <UserPlusIcon className="w-4 h-4" />
                    {addableBuildCounts.total
                      ? `Add to this job (${addableBuildCounts.total})`
                      : 'Add to this job'}
                  </button>
                )}
                {paused && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleResume()}
                    className="btn-primary !py-2 !px-4 text-sm inline-flex items-center gap-1.5"
                  >
                    <PlayIcon className="w-4 h-4" /> Resume
                  </button>
                )}
                {running && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handlePause()}
                    className="btn-secondary !py-2 !px-4 text-sm inline-flex items-center gap-1.5"
                  >
                    <PauseIcon className="w-4 h-4" /> Pause
                  </button>
                )}
                {(running || paused) && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleStop()}
                    className="btn-secondary !py-2 !px-4 text-sm inline-flex items-center gap-1.5 text-red-600 dark:text-red-400"
                  >
                    <StopIcon className="w-4 h-4" /> Stop
                  </button>
                )}
                <button
                  type="button"
                  disabled={coldBusy}
                  onClick={handleStopColdAccounts}
                  title="Ejects accounts that have sent 100 follows since their last follow-back. They stop within a minute and stay out of future runs."
                  className="btn-secondary !py-2 !px-4 text-sm inline-flex items-center gap-1.5"
                >
                  <NoSymbolIcon className="w-4 h-4" />
                  {coldBusy ? 'Stopping…' : 'Stop cold accounts'}
                </button>
              </div>
            </div>

            {!running && !paused && (
              <label
                className="text-xs inline-flex items-center gap-2 cursor-pointer text-gray-500"
                title="Cold = 100 follows sent since the last follow-back, or none ever."
              >
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={excludeCold}
                  onChange={(e) => setExcludeCold(e.target.checked)}
                />
                Leave out accounts with no follow-backs in their last 100 follows
              </label>
            )}

            <div className="space-y-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">User profiles</div>
                    <Link
                      to="/dashboard/admin/vps/user-profiles"
                      className="text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      Add / edit catalog
                    </Link>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="btn-secondary !py-2 !px-3 text-sm"
                      disabled={assignBusy || busy || occupancy.available < 1}
                      onClick={() => handleAutoAssign({
                        allUnattached: true,
                        profileIds: selectedProfileIds.length ? selectedProfileIds : null
                      })}
                      title={occupancy.available < 1
                        ? 'No logged-in accounts on this VPS are free of a user-profile'
                        : selectedProfileIds.length
                          ? 'Spread every unattached account across the ticked profiles on this VPS'
                          : 'Spread every unattached account across the catalog on this VPS'}
                    >
                      {assignBusy ? 'Assigning…' : selectedProfileIds.length
                        ? 'Spread unattached across selected'
                        : 'Spread unattached across catalog'}
                    </button>
                    <label className="text-xs text-gray-500 inline-flex items-center gap-2">
                      or
                      <input
                        type="number"
                        min={1}
                        className="form-input text-sm w-20"
                        value={autoAssignAmount}
                        onChange={(e) => setAutoAssignAmount(Number(e.target.value) || 1)}
                      />
                    </label>
                    <button
                      type="button"
                      className="btn-secondary !py-2 !px-3 text-sm"
                      disabled={assignBusy || busy || occupancy.available < 1}
                      onClick={() => handleAutoAssign({
                        amount: autoAssignAmount,
                        profileIds: selectedProfileIds.length ? selectedProfileIds : null
                      })}
                    >
                      Spread {autoAssignAmount}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary !py-2 !px-3 text-sm text-red-600 dark:text-red-400"
                      disabled={
                        assignBusy
                        || busy
                        || running
                        || paused
                        || (selectedProfileIds.length
                          ? !selectedProfileIds.some((id) => (occupancyByProfile.get(String(id)) || 0) > 0)
                          : occupancy.attached < 1)
                      }
                      onClick={() => {
                        if (selectedProfileIds.length) {
                          const count = selectedProfileIds.reduce(
                            (n, id) => n + (occupancyByProfile.get(String(id)) || 0),
                            0
                          );
                          handleUnassignProfiles({
                            profileIds: selectedProfileIds,
                            count
                          });
                          return;
                        }
                        handleUnassignProfiles({ count: occupancy.attached });
                      }}
                      title="Clears the user-profile sticker on this VPS. Accounts stay attached to the agent."
                    >
                      {selectedProfileIds.length ? 'Remove from selected' : 'Remove all on this VPS'}
                    </button>
                  </div>
                </div>
                <p className="text-[11px] text-gray-500">
                  {fmt(occupancy.loggedIn)} logged in on this VPS
                  {' · '}
                  {fmt(occupancy.attached)} on a user-profile
                  {' · '}
                  {fmt(occupancy.available)} available to assign
                  . Cap is per VPS — each agent fills 0 → max independently.
                  {(running || paused) && (
                    <> Tick a profile that is not already in this job, then Add to this job.</>
                  )}
                </p>
                {rankedUserProfiles.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
                    {rankedUserProfiles.map((profile) => {
                      const pid = String(profile._id);
                      const onVps = loggedInAccounts.filter(
                        (row) => String(row.userProfileId) === pid
                      );
                      const onVpsCount = occupancyByProfile.get(pid) || onVps.length;
                      const attached = onVpsCount > 0;
                      const ticked = selectedProfileIds.includes(pid);
                      const perAmount = Math.max(1, Number(assignByProfile[pid]) || 500);
                      return (
                        <div
                          key={profile._id}
                          className={`rounded-xl border-2 px-3 py-2.5 space-y-2 ${
                            attached
                              ? `border-blue-600 dark:border-blue-500 ${
                                  ticked ? 'bg-blue-50/70 dark:bg-blue-900/20' : ''
                                }`
                              : ticked
                                ? 'border-blue-400 bg-blue-50/70 dark:bg-blue-900/20'
                                : 'border-gray-200 dark:border-gray-700'
                          }`}
                        >
                          <label className="flex items-start gap-2 min-w-0 cursor-pointer">
                            <input
                              type="checkbox"
                              className="form-checkbox mt-0.5"
                              checked={ticked}
                              disabled={busy}
                              onChange={() => toggleProfileSelected(profile._id)}
                              aria-label={`Include ${profile.name} in this run`}
                            />
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {profile.name}
                              </div>
                              <div className="text-[11px] text-gray-500">
                                {fmt(onVpsCount)}/{fmt(profile.maxAccounts)} on this VPS
                                {' · '}
                                {labelFollowRunner(profile.followRunner)}
                                {' · '}
                                {profile.inputDevice}
                                {' · '}
                                {profile.handedness}
                              </div>
                            </div>
                          </label>
                          <div className="flex flex-wrap items-center gap-2">
                            <input
                              type="number"
                              min={1}
                              className="form-input text-xs w-16 !py-1"
                              value={perAmount}
                              onChange={(e) => setAssignByProfile((prev) => ({
                                ...prev,
                                [pid]: Math.max(1, Number(e.target.value) || 1)
                              }))}
                              aria-label={`Accounts to assign to ${profile.name}`}
                            />
                            <button
                              type="button"
                              className="btn-secondary !py-1 !px-2 text-xs"
                              disabled={assignBusy || busy || occupancy.available < 1}
                              onClick={() => handleAutoAssign({
                                profileId: pid,
                                amount: perAmount,
                                profileName: profile.name
                              })}
                            >
                              Assign on this VPS
                            </button>
                            <button
                              type="button"
                              className="btn-secondary !py-1 !px-2 text-xs text-red-600 dark:text-red-400"
                              disabled={assignBusy || busy || running || paused || onVpsCount < 1}
                              onClick={() => handleUnassignProfiles({
                                profileId: pid,
                                profileName: profile.name,
                                count: onVpsCount
                              })}
                            >
                              Remove
                            </button>
                            <button
                              type="button"
                              className="btn-secondary !py-1 !px-2 text-xs"
                              disabled={busy || running || paused || !onVps.length}
                              onClick={() => handleTestProfile(profile)}
                            >
                              Test
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {!userProfiles.length && (
                  <p className="text-xs text-gray-500">
                    No user profiles yet — add them in the catalog, then assign accounts here.
                  </p>
                )}
              </div>

            {(running || paused) && (
              <div className="flex flex-wrap items-end gap-3 mb-3">
                <label className="text-xs space-y-1 min-w-[16rem]">
                  <span className="text-gray-500 uppercase tracking-wide">Database</span>
                  <select
                    className="form-select text-sm w-full"
                    value={workspaceId}
                    onChange={(e) => setWorkspaceId(e.target.value)}
                  >
                    {(workspaces.length ? workspaces : [{ workspaceId: 'legacy', name: 'Current data (legacy)', followBacks: 0 }]).map((w) => (
                      <option key={w.workspaceId} value={w.workspaceId}>
                        {w.name} — {(Number(w.followBacks) || 0).toLocaleString()} follow-backs
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  disabled={busy}
                  onClick={handleApplyTargetFilters}
                  className="px-3 py-2 rounded-lg text-xs font-medium bg-indigo-50 text-indigo-800 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-200 disabled:opacity-50"
                >
                  Switch database
                </button>
              </div>
            )}
            {!running && !paused && (
              <>
                <SettingsDrawer
                  open={followSettingsOpen}
                  onToggle={() => setFollowSettingsOpen((v) => !v)}
                  title="Follow automation settings"
                  subtitle={followSettingsSummary}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                <label className="text-xs space-y-1">
                  <span className="text-gray-500 uppercase tracking-wide">Follow model</span>
                  <select
                    className="form-select text-sm w-full"
                    value={followModelId}
                    onChange={(e) => setFollowModelId(e.target.value)}
                  >
                    <option value="">Select model…</option>
                    {models.map((m) => (
                      <option key={m._id} value={m._id}>
                        {m.displayName || m.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs space-y-1">
                  <span className="text-gray-500 uppercase tracking-wide">Database</span>
                  <select
                    className="form-select text-sm w-full"
                    value={workspaceId}
                    onChange={(e) => setWorkspaceId(e.target.value)}
                  >
                    {(workspaces.length ? workspaces : [{ workspaceId: 'legacy', name: 'Current data (legacy)', followBacks: 0 }]).map((w) => (
                      <option key={w.workspaceId} value={w.workspaceId}>
                        {w.name} — {(Number(w.followBacks) || 0).toLocaleString()} follow-backs
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs space-y-1">
                  <span className="text-gray-500 uppercase tracking-wide">Target source</span>
                  <select
                    className="form-select text-sm w-full"
                    value={sourceType}
                    onChange={(e) => setSourceType(e.target.value)}
                  >
                    <option value="scrape_v2_pool">Scrape V2 pool</option>
                    <option value="scrape_v2_session">Scrape V2 session</option>
                    <option value="scrape_tag">Scrape tag</option>
                    <option value="paste">Paste list</option>
                  </select>
                </label>
                <label className="text-xs space-y-1">
                  <span className="text-gray-500 uppercase tracking-wide">Category / tag</span>
                  <select
                    className="form-select text-sm w-full"
                    value={scrapeTag}
                    onChange={(e) => setScrapeTag(e.target.value)}
                  >
                    <option value="">
                      {breakdown?.allTagsTotal != null
                        ? `All categories (${Number(breakdown.allTagsTotal).toLocaleString()})`
                        : 'All categories'}
                    </option>
                    {(breakdown?.tags || []).map((t) => (
                      <option key={t.tag} value={t.tag}>
                        {t.tag} ({Number(t.available).toLocaleString()})
                      </option>
                    ))}
                    {/* Keep a saved tag selectable even when it has nothing available. */}
                    {scrapeTag && !(breakdown?.tags || []).some((t) => t.tag === scrapeTag) && (
                      <option value={scrapeTag}>{scrapeTag}</option>
                    )}
                  </select>
                </label>
                <label className="text-xs space-y-1">
                  <span className="text-gray-500 uppercase tracking-wide">Max browsers</span>
                  <input
                    type="number"
                    min={1}
                    max={1000}
                    className="form-input text-sm w-full"
                    value={config.maxConcurrentBrowsers}
                    onChange={(e) => setConfig((c) => ({
                      ...c,
                      maxConcurrentBrowsers: Number(e.target.value) || 1
                    }))}
                  />
                </label>
                <label className="text-xs space-y-1">
                  <span className="text-gray-500 uppercase tracking-wide">Round follows min–max</span>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      className="form-input text-sm w-full"
                      value={config.followsPerAccountMin}
                      onChange={(e) => setConfig((c) => ({
                        ...c,
                        followsPerAccountMin: Number(e.target.value) || 1
                      }))}
                    />
                    <input
                      type="number"
                      className="form-input text-sm w-full"
                      value={config.followsPerAccountMax}
                      onChange={(e) => setConfig((c) => ({
                        ...c,
                        followsPerAccountMax: Number(e.target.value) || 1
                      }))}
                    />
                  </div>
                </label>
                <label className="text-xs space-y-1">
                  <span className="text-gray-500 uppercase tracking-wide">Daily total min–max</span>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      className="form-input text-sm w-full"
                      value={config.totalFollowsPerProfileMin}
                      onChange={(e) => setConfig((c) => ({
                        ...c,
                        totalFollowsPerProfileMin: Number(e.target.value) || 1
                      }))}
                    />
                    <input
                      type="number"
                      className="form-input text-sm w-full"
                      value={config.totalFollowsPerProfileMax}
                      onChange={(e) => setConfig((c) => ({
                        ...c,
                        totalFollowsPerProfileMax: Number(e.target.value) || 1
                      }))}
                    />
                  </div>
                </label>
                <label className="text-xs space-y-1">
                  <span className="text-gray-500 uppercase tracking-wide">Wait between rounds (minutes min–max)</span>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min={0}
                      className="form-input text-sm w-full"
                      value={minutesFromMs(config.roundGapMinMs)}
                      onChange={(e) => setGapMinutes('roundGapMinMs', e.target.value)}
                    />
                    <input
                      type="number"
                      min={0}
                      className="form-input text-sm w-full"
                      value={minutesFromMs(config.roundGapMaxMs)}
                      onChange={(e) => setGapMinutes('roundGapMaxMs', e.target.value)}
                    />
                  </div>
                  <span className="text-[11px] text-gray-400">
                    0–0 = next round starts immediately
                  </span>
                </label>
                  </div>
                  {sourceType === 'scrape_v2_pool' && (
                    <FollowBacksFirstFields
                      enabled={fbfEnabled}
                      onEnabled={setFbfEnabled}
                      sourceModelId={fbfSourceModelId}
                      onSourceModelId={setFbfSourceModelId}
                      minAgeDays={fbfMinAgeDays}
                      onMinAgeDays={setFbfMinAgeDays}
                      models={models.filter((m) => String(m._id) !== String(followModelId))}
                      modelLabel={(m) => m.displayName || m.name}
                    />
                  )}
                </SettingsDrawer>
                <SettingsDrawer
                  open={targetFiltersOpen}
                  onToggle={() => setTargetFiltersOpen((v) => !v)}
                  title="Target filters"
                  subtitle={sourceType === 'scrape_v2_pool' ? targetFiltersSummary : undefined}
                >
                  <div className="text-xs space-y-4">
                    {sourceType === 'scrape_v2_pool' && (
                      <>
                    <div className="space-y-2">
                      <span className="text-gray-500 uppercase tracking-wide">Signed up on</span>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {(breakdown?.platforms || DEFAULT_PLATFORMS).map((p) => {
                          const checked = platforms.includes(p.platform);
                          return (
                            <label
                              key={p.platform}
                              className={`flex items-center gap-2 rounded border px-2 py-1.5 cursor-pointer transition-colors ${
                                checked
                                  ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                                  : 'border-gray-200 dark:border-gray-700'
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="form-checkbox h-3.5 w-3.5"
                                checked={checked}
                                onChange={() => togglePlatform(p.platform)}
                              />
                              <span className="flex-1 min-w-0">
                                <span className="block truncate">{p.label}</span>
                                <span className="block text-gray-500">
                                  {p.available == null ? '—' : Number(p.available).toLocaleString()}
                                </span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="form-checkbox h-3.5 w-3.5"
                          checked={includeUnknownPlatform}
                          onChange={(e) => setIncludeUnknownPlatform(e.target.checked)}
                        />
                        <span>
                          Include accounts with no recorded platform
                          {breakdown?.unknownPlatform != null && (
                            <span className="text-gray-500">
                              {' '}({Number(breakdown.unknownPlatform).toLocaleString()})
                            </span>
                          )}
                        </span>
                      </label>
                    </div>

                    <div className="space-y-2">
                      <span className="text-gray-500 uppercase tracking-wide">Account age</span>
                      <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <span className="text-sm text-gray-800 dark:text-gray-100">
                          {ageFrom === ageTo
                            ? ageBucketLabel(ageFrom)
                            : `${ageBucketLabel(ageFrom)} \u2192 ${ageBucketLabel(ageTo)}`}
                        </span>
                        {(ageFrom !== 0 || ageTo !== MAX_AGE_BUCKET) && (
                          <button
                            type="button"
                            className="text-blue-600 hover:underline"
                            onClick={() => setAgeBuckets(FULL_AGE_RANGE)}
                          >
                            Reset to all ages
                          </button>
                        )}
                      </div>
                      <div className="px-2">
                        <AgeRangeSlider
                          from={ageFrom}
                          to={ageTo}
                          max={MAX_AGE_BUCKET}
                          onChange={setAgeRange}
                        />
                        <div className="flex justify-between text-gray-500">
                          <span>Under 1y</span>
                          <span>10+y</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                        {(breakdown?.ageBuckets || DEFAULT_AGE_BUCKETS).map((b) => {
                          const inRange = b.bucket >= ageFrom && b.bucket <= ageTo;
                          return (
                            <div
                              key={b.bucket}
                              className={`rounded border px-2 py-1.5 transition-colors ${
                                inRange
                                  ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                                  : 'border-gray-200 dark:border-gray-700 opacity-50'
                              }`}
                            >
                              <span className="block truncate">{b.label}</span>
                              <span className="block text-gray-500">
                                {b.available == null ? '—' : Number(b.available).toLocaleString()}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="form-checkbox h-3.5 w-3.5"
                          checked={includeUnknownAge}
                          onChange={(e) => setIncludeUnknownAge(e.target.checked)}
                        />
                        <span>
                          Include accounts with unknown age
                          {breakdown?.unknownAge != null && (
                            <span className="text-gray-500">
                              {' '}({Number(breakdown.unknownAge).toLocaleString()})
                            </span>
                          )}
                        </span>
                      </label>
                    </div>

                    {isCleanSheetWorkspace ? (
                      <FollowTargetSignalFilters
                        value={signalFilters}
                        onChange={setSignalFilters}
                        breakdown={breakdown}
                      />
                    ) : (
                      <p className="text-gray-500">
                        Conversion-signal filters (tweets, last active, blue, creator links, …)
                        are available on a clean-sheet database. Pick one above, or start a
                        new scrape with Clean sheet ticked.
                      </p>
                    )}

                    {(!includeUnknownAge || !includeUnknownPlatform) && (
                      <p className="text-amber-600 dark:text-amber-500">
                        Accounts scraped before these signals were captured have
                        empty fields, and will be skipped when unknown is off.
                      </p>
                    )}
                    {isLiveAgentStatus(dash?.agent?.status) && (
                      <button
                        type="button"
                        className="text-blue-600 hover:underline disabled:opacity-50"
                        disabled={busy}
                        onClick={handleApplyTargetFilters}
                      >
                        Apply to running job
                      </button>
                    )}
                      </>
                    )}
                    <div className="space-y-2">
                      <span className="text-gray-500 uppercase tracking-wide">Pre-follow warm-up</span>
                      <div className="flex flex-wrap gap-x-6 gap-y-2">
                        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 dark:border-gray-600"
                            checked={config.warmupVisitNotifications !== false}
                            onChange={(e) => setConfig((c) => ({
                              ...c,
                              warmupVisitNotifications: e.target.checked
                            }))}
                          />
                          <span>Visit /notifications</span>
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 dark:border-gray-600"
                            checked={config.warmupVisitSettings !== false}
                            onChange={(e) => setConfig((c) => ({
                              ...c,
                              warmupVisitSettings: e.target.checked
                            }))}
                          />
                          <span>Visit /settings/account</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </SettingsDrawer>
                {sourceType === 'scrape_v2_pool' && (
                  <SettingsDrawer
                    open={autoOptimizeOpen}
                    onToggle={() => setAutoOptimizeOpen((v) => !v)}
                    title="Auto optimize sources"
                    subtitle={(() => {
                      if (!autoOptEnabled) return 'Off — dispatch is uniform FIFO across all sources';
                      if (lifeEnabled) {
                        const c = lifeSummary?.counts;
                        return c
                          ? `Bounded spend · ${c.proven} proven · ${c.trial} spending · ${c.awaiting_verdict} maturing · ${c.parked} parked`
                          : `Bounded spend · ${lifeTrialSlots} trials × ${lifeTrialBudget} follows`;
                      }
                      return autoOptSummary?.lastRefreshedAt
                        ? `On · ${autoOptSummary.topSources?.length || 0} top · ${autoOptSummary.explorationSources?.length || 0} explore · updated ${new Date(autoOptSummary.lastRefreshedAt).toLocaleTimeString()}`
                        : 'On · awaiting first refresh';
                    })()}
                  >
                    <div className="text-xs space-y-4">
                      <p className="text-gray-500">
                        {lifeEnabled ? (
                          <>
                            Bounded spend is on, so dispatch runs off per-source budgets
                            and live follow-back rates rather than the cohort settings
                            below. Those still matter as the fallback whenever the
                            lifecycle cannot fill a batch, and
                            {' '}{Math.round(autoOptExplorationRatio * 100)}% of each batch
                            stays reserved for discovery either way.
                          </>
                        ) : (
                          <>
                            Ranks Source Performance for this workspace, focuses follow
                            dispatch on the top {autoOptTopN} sources, and reserves
                            {' '}{Math.round(autoOptExplorationRatio * 100)}% of each
                            batch for exploring untested sources.
                          </>
                        )}
                      </p>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="form-checkbox h-3.5 w-3.5"
                          checked={autoOptEnabled}
                          onChange={(e) => setAutoOptEnabled(e.target.checked)}
                        />
                        <span className="font-medium">Enable auto-optimize</span>
                      </label>

                      {autoOptEnabled && (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <label className="flex flex-col gap-1">
                              <span className="text-gray-500 uppercase tracking-wide">Ranking metric</span>
                              <select
                                className="form-select text-sm w-full"
                                value={autoOptMetric}
                                onChange={(e) => setAutoOptMetric(e.target.value)}
                              >
                                <option value="followback">Follow-back rate</option>
                                <option value="conversation">Follow → conversation</option>
                                <option value="cta">Conversation → CTA</option>
                                <option value="composite">Composite (0.5 FB / 0.3 Conv / 0.2 CTA)</option>
                              </select>
                            </label>
                            <label className="flex flex-col gap-1">
                              <span className="text-gray-500 uppercase tracking-wide">Refresh cadence (min)</span>
                              <select
                                className="form-select text-sm w-full"
                                value={autoOptRefreshMinutes}
                                onChange={(e) => setAutoOptRefreshMinutes(Number(e.target.value))}
                              >
                                {[10, 30, 60, 120].map((m) => (
                                  <option key={m} value={m}>{m} min</option>
                                ))}
                              </select>
                            </label>
                            <label className="flex flex-col gap-1">
                              <span className="text-gray-500 uppercase tracking-wide">Top-N sources</span>
                              <input
                                type="number"
                                min={5}
                                max={500}
                                className="form-input text-sm w-full"
                                value={autoOptTopN}
                                onChange={(e) => setAutoOptTopN(Math.max(5, Math.min(500, Number(e.target.value) || 50)))}
                              />
                            </label>
                            <label className="flex flex-col gap-1">
                              <span className="text-gray-500 uppercase tracking-wide">Exploration (0–0.5)</span>
                              <input
                                type="number"
                                min={0}
                                max={0.5}
                                step={0.05}
                                className="form-input text-sm w-full"
                                value={autoOptExplorationRatio}
                                onChange={(e) => setAutoOptExplorationRatio(Math.max(0, Math.min(0.5, Number(e.target.value) || 0)))}
                              />
                            </label>
                            <label className="flex flex-col gap-1">
                              <span className="text-gray-500 uppercase tracking-wide">Min follows to trust source</span>
                              <input
                                type="number"
                                min={5}
                                max={500}
                                className="form-input text-sm w-full"
                                value={autoOptMinSampleFollows}
                                onChange={(e) => setAutoOptMinSampleFollows(Math.max(5, Math.min(500, Number(e.target.value) || 300)))}
                              />
                            </label>
                            <label className="flex flex-col gap-1">
                              <span className="text-gray-500 uppercase tracking-wide">Cool-down FB floor (%)</span>
                              <input
                                type="number"
                                min={0}
                                max={20}
                                step={0.25}
                                className="form-input text-sm w-full"
                                value={autoOptCoolDownFloor}
                                onChange={(e) => setAutoOptCoolDownFloor(Math.max(0, Math.min(20, Number(e.target.value) || 3)))}
                              />
                            </label>
                          </div>

                          <label className="flex items-center gap-2 cursor-pointer text-gray-600">
                            <input
                              type="checkbox"
                              className="form-checkbox h-3.5 w-3.5"
                              checked={autoOptFallback}
                              onChange={(e) => setAutoOptFallback(e.target.checked)}
                            />
                            <span>Fallback to unconstrained FIFO if cohorts exhausted (default off — prefer under-dispatch)</span>
                          </label>

                          <div className="rounded border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/10 p-3 space-y-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                className="form-checkbox h-3.5 w-3.5"
                                checked={autoOptGateEnabled}
                                onChange={(e) => setAutoOptGateEnabled(e.target.checked)}
                              />
                              <span className="font-medium">Also gate scraping</span>
                            </label>
                            <p className="text-gray-500 leading-relaxed">
                              Sources under {autoOptGatePauseBelow}% follow-back after {autoOptGatePauseAfter}
                              {' '}dispatched follows are paused. Passively re-scored every {autoOptGateRescoreHours}h
                              using the live follow-back ledger (no probe follows spent). Resumed
                              automatically at {'\u2265'}{autoOptGateResumeAt}%. Permanently excluded only after
                              {' '}{autoOptGateExcludeDays}+ days paused with rate still under {autoOptGateExcludeUnder}%.
                            </p>
                            {autoOptGateEnabled && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <label className="flex flex-col gap-1">
                                  <span className="text-gray-500 uppercase tracking-wide">Pause after N follows</span>
                                  <input
                                    type="number"
                                    min={50}
                                    max={5000}
                                    className="form-input text-sm w-full"
                                    value={autoOptGatePauseAfter}
                                    onChange={(e) => setAutoOptGatePauseAfter(Math.max(50, Math.min(5000, Number(e.target.value) || 400)))}
                                  />
                                </label>
                                <label className="flex flex-col gap-1">
                                  <span className="text-gray-500 uppercase tracking-wide">Pause below FB (%)</span>
                                  <input
                                    type="number"
                                    min={0}
                                    max={20}
                                    step={0.25}
                                    className="form-input text-sm w-full"
                                    value={autoOptGatePauseBelow}
                                    onChange={(e) => setAutoOptGatePauseBelow(Math.max(0, Math.min(20, Number(e.target.value) || 1.75)))}
                                  />
                                </label>
                                <label className="flex flex-col gap-1">
                                  <span className="text-gray-500 uppercase tracking-wide">Resume at FB (%)</span>
                                  <input
                                    type="number"
                                    min={0}
                                    max={20}
                                    step={0.25}
                                    className="form-input text-sm w-full"
                                    value={autoOptGateResumeAt}
                                    onChange={(e) => setAutoOptGateResumeAt(Math.max(0, Math.min(20, Number(e.target.value) || 2.0)))}
                                  />
                                </label>
                                <label className="flex flex-col gap-1">
                                  <span className="text-gray-500 uppercase tracking-wide">Rescore cadence (hours)</span>
                                  <input
                                    type="number"
                                    min={1}
                                    max={72}
                                    className="form-input text-sm w-full"
                                    value={autoOptGateRescoreHours}
                                    onChange={(e) => setAutoOptGateRescoreHours(Math.max(1, Math.min(72, Number(e.target.value) || 6)))}
                                  />
                                </label>
                                <label className="flex flex-col gap-1">
                                  <span className="text-gray-500 uppercase tracking-wide">Auto-exclude after (days)</span>
                                  <input
                                    type="number"
                                    min={7}
                                    max={365}
                                    className="form-input text-sm w-full"
                                    value={autoOptGateExcludeDays}
                                    onChange={(e) => setAutoOptGateExcludeDays(Math.max(7, Math.min(365, Number(e.target.value) || 60)))}
                                  />
                                </label>
                                <label className="flex flex-col gap-1">
                                  <span className="text-gray-500 uppercase tracking-wide">Auto-exclude under (%)</span>
                                  <input
                                    type="number"
                                    min={0}
                                    max={5}
                                    step={0.25}
                                    className="form-input text-sm w-full"
                                    value={autoOptGateExcludeUnder}
                                    onChange={(e) => setAutoOptGateExcludeUnder(Math.max(0, Math.min(5, Number(e.target.value) || 1.0)))}
                                  />
                                </label>
                              </div>
                            )}
                            {autoOptGateEnabled && autoOptGateResumeAt <= autoOptGatePauseBelow && (
                              <p className="text-red-600 dark:text-red-500">
                                Resume threshold must be greater than pause threshold — hysteresis prevents ping-pong.
                              </p>
                            )}
                            {autoOptGateEnabled && autoOptSummary && (
                              <p className="text-gray-500">
                                Currently paused: {autoOptSummary.pausedCount} · excluded: {autoOptSummary.excludedCount}
                                {autoOptSummary.lastRescoredAt
                                  ? ` · last rescored ${new Date(autoOptSummary.lastRescoredAt).toLocaleTimeString()}`
                                  : ''}
                              </p>
                            )}
                          </div>

                          <div className="rounded border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/10 p-3 space-y-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                className="form-checkbox h-3.5 w-3.5"
                                checked={lifeEnabled}
                                onChange={(e) => setLifeEnabled(e.target.checked)}
                              />
                              <span className="font-medium">Bounded-spend dispatch (recommended)</span>
                            </label>
                            <p className="text-gray-500 leading-relaxed">
                              Replaces the top/explore split above with a per-source budget
                              charged the moment rows are claimed, so no source can absorb
                              more than its trial allowance before a verdict exists. Each
                              trial gets {lifeTrialBudget} follows; once spent it frees its
                              slot immediately and a new source starts testing, so discovery
                              never pauses while verdicts settle. Follows are discounted by
                              how long they have had to convert, which lets a clearly good
                              source be promoted within hours while a dead one is only parked
                              once its follows have actually reported in. Above
                              {' '}{lifePromoteAt}% it becomes proven and shares each batch in
                              proportion to its live rate; below {lifeParkBelow}% it is parked
                              and its unfollowed claims are released. In between it gets one
                              more {lifeExtensionBudget}-follow round.
                            </p>
                            <p className="text-gray-400 leading-relaxed">
                              Throughput is set by how fast the fleet spends, not by the slot
                              count: at {lifeTrialBudget} follows per trial that is roughly one
                              verdict per {lifeTrialBudget.toLocaleString()} follows dispatched,
                              whatever you set below.
                            </p>
                            {lifeEnabled && (
                              <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <label className="flex flex-col gap-1">
                                    <span className="text-gray-500 uppercase tracking-wide">
                                      Trials spending at once
                                    </span>
                                    <input
                                      type="number"
                                      min={1}
                                      max={500}
                                      className="form-input text-sm w-full"
                                      value={lifeTrialSlots}
                                      onChange={(e) => setLifeTrialSlots(Math.max(1, Math.min(500, Number(e.target.value) || 20)))}
                                    />
                                    <span className="text-gray-400 normal-case">
                                      How many trials are part-filled at once
                                      ({(lifeTrialSlots * lifeTrialBudget).toLocaleString()} follows
                                      at risk). Sources awaiting a verdict do not hold a slot.
                                    </span>
                                  </label>
                                  <label className="flex flex-col gap-1">
                                    <span className="text-gray-500 uppercase tracking-wide">
                                      Max sources under test
                                    </span>
                                    <input
                                      type="number"
                                      min={50}
                                      max={20000}
                                      step={50}
                                      className="form-input text-sm w-full"
                                      value={lifeMaxInTest}
                                      onChange={(e) => setLifeMaxInTest(Math.max(50, Math.min(20000, Number(e.target.value) || 2000)))}
                                    />
                                    <span className="text-gray-400 normal-case">
                                      Ceiling on trials plus those awaiting a verdict — a backstop
                                      in case follow-back detection ever stalls.
                                    </span>
                                  </label>
                                  <label className="flex flex-col gap-1">
                                    <span className="text-gray-500 uppercase tracking-wide">Trial budget (follows)</span>
                                    <input
                                      type="number"
                                      min={50}
                                      max={5000}
                                      className="form-input text-sm w-full"
                                      value={lifeTrialBudget}
                                      onChange={(e) => setLifeTrialBudget(Math.max(50, Math.min(5000, Number(e.target.value) || 300)))}
                                    />
                                  </label>
                                  <label className="flex flex-col gap-1">
                                    <span className="text-gray-500 uppercase tracking-wide">Extension budget</span>
                                    <input
                                      type="number"
                                      min={0}
                                      max={10000}
                                      className="form-input text-sm w-full"
                                      value={lifeExtensionBudget}
                                      onChange={(e) => setLifeExtensionBudget(Math.max(0, Math.min(10000, Number(e.target.value) || 0)))}
                                    />
                                  </label>
                                  <label className="flex flex-col gap-1">
                                    <span className="text-gray-500 uppercase tracking-wide">
                                      Re-check gap (hours)
                                    </span>
                                    <input
                                      type="number"
                                      min={1}
                                      max={168}
                                      className="form-input text-sm w-full"
                                      value={lifeMaturityHours}
                                      onChange={(e) => setLifeMaturityHours(Math.max(1, Math.min(168, Number(e.target.value) || 6)))}
                                    />
                                    <span className="text-gray-400 normal-case">
                                      How long before a spent trial is first read, and the gap
                                      between re-reads while it is still inconclusive. Not a
                                      freeze — immature follows are discounted, not ignored.
                                    </span>
                                  </label>
                                  <label className="flex flex-col gap-1">
                                    <span className="text-gray-500 uppercase tracking-wide">Promote at FB (%)</span>
                                    <input
                                      type="number"
                                      min={0.1}
                                      max={50}
                                      step={0.25}
                                      className="form-input text-sm w-full"
                                      value={lifePromoteAt}
                                      onChange={(e) => setLifePromoteAt(Math.max(0.1, Math.min(50, Number(e.target.value) || 3)))}
                                    />
                                  </label>
                                  <label className="flex flex-col gap-1">
                                    <span className="text-gray-500 uppercase tracking-wide">Demote proven below (%)</span>
                                    <input
                                      type="number"
                                      min={0}
                                      max={50}
                                      step={0.25}
                                      className="form-input text-sm w-full"
                                      value={lifeDemoteBelow}
                                      onChange={(e) => setLifeDemoteBelow(Math.max(0, Math.min(50, Number(e.target.value) || 2)))}
                                    />
                                  </label>
                                  <label className="flex flex-col gap-1">
                                    <span className="text-gray-500 uppercase tracking-wide">Park trial below (%)</span>
                                    <input
                                      type="number"
                                      min={0}
                                      max={50}
                                      step={0.25}
                                      className="form-input text-sm w-full"
                                      value={lifeParkBelow}
                                      onChange={(e) => setLifeParkBelow(Math.max(0, Math.min(50, Number(e.target.value) || 1)))}
                                    />
                                  </label>
                                  <label className="flex flex-col gap-1">
                                    <span className="text-gray-500 uppercase tracking-wide">Min follows to judge</span>
                                    <input
                                      type="number"
                                      min={10}
                                      max={5000}
                                      className="form-input text-sm w-full"
                                      value={lifeMinVerdictFollows}
                                      onChange={(e) => setLifeMinVerdictFollows(Math.max(10, Math.min(5000, Number(e.target.value) || 50)))}
                                    />
                                  </label>
                                  <label className="flex flex-col gap-1">
                                    <span className="text-gray-500 uppercase tracking-wide">
                                      Score proven over (days)
                                    </span>
                                    <input
                                      type="number"
                                      min={1}
                                      max={90}
                                      className="form-input text-sm w-full"
                                      value={lifeProvenWindow}
                                      onChange={(e) => setLifeProvenWindow(Math.max(1, Math.min(90, Number(e.target.value) || 7)))}
                                    />
                                    <span className="text-gray-400 normal-case">
                                      Trailing window for a proven source's rate. Lifetime numbers
                                      hide a collapse behind months of good history; this is what
                                      lets a sudden drop cost the source its share of the batch.
                                    </span>
                                  </label>
                                  <label className="flex flex-col gap-1">
                                    <span className="text-gray-500 uppercase tracking-wide">Re-test parked after (days)</span>
                                    <input
                                      type="number"
                                      min={1}
                                      max={365}
                                      className="form-input text-sm w-full"
                                      value={lifeRetestDays}
                                      onChange={(e) => setLifeRetestDays(Math.max(1, Math.min(365, Number(e.target.value) || 30)))}
                                    />
                                  </label>
                                  <label className="flex flex-col gap-1">
                                    <span className="text-gray-500 uppercase tracking-wide">
                                      Re-scrape proven below (rows) · 0 = off
                                    </span>
                                    <input
                                      type="number"
                                      min={0}
                                      max={100000}
                                      step={100}
                                      className="form-input text-sm w-full"
                                      value={lifeRescrapeBelow}
                                      onChange={(e) => setLifeRescrapeBelow(Math.max(0, Math.min(100000, Number(e.target.value) || 0)))}
                                    />
                                    <span className="text-gray-400 normal-case">
                                      Off by default — nothing consumes the queue yet, and re-walking a
                                      finished source costs a full crawl for only its newest followers.
                                    </span>
                                  </label>
                                </div>
                                <label className="flex items-center gap-2 cursor-pointer text-gray-600">
                                  <input
                                    type="checkbox"
                                    className="form-checkbox h-3.5 w-3.5"
                                    checked={lifeWilson}
                                    onChange={(e) => setLifeWilson(e.target.checked)}
                                  />
                                  <span>
                                    Judge on statistical bounds rather than the raw rate — stops a lucky
                                    {' '}{lifeTrialBudget}-follow sample from promoting a mediocre source
                                  </span>
                                </label>
                                {!(lifePromoteAt > lifeDemoteBelow && lifeDemoteBelow >= lifeParkBelow) && (
                                  <p className="text-red-600 dark:text-red-500">
                                    Thresholds must satisfy promote {'>'} demote {'\u2265'} park, otherwise
                                    the same measurement both promotes and demotes a source.
                                  </p>
                                )}
                                {lifeSummary?.counts && (
                                  <p className="text-gray-500">
                                    {lifeSummary.counts.proven} proven · {lifeSummary.counts.trial} on trial ·
                                    {' '}{lifeSummary.counts.awaiting_verdict} maturing ·
                                    {' '}{lifeSummary.counts.parked} parked ·
                                    {' '}{lifeSummary.slotsUsed}/{lifeSummary.slotsTotal} spending
                                    {lifeSummary.maxInTest
                                      ? ` · ${lifeSummary.inTest}/${lifeSummary.maxInTest} under test`
                                      : ''}
                                    {lifeSummary.rescrapeQueued
                                      ? ` · ${lifeSummary.rescrapeQueued} queued for re-scrape`
                                      : ''}
                                  </p>
                                )}
                              </>
                            )}
                          </div>

                          {autoOptSummary?.lastScoreSummary && (
                            <p className="text-gray-500">
                              Score summary — ranked {autoOptSummary.lastScoreSummary.rankedRows || 0} sources,
                              median {autoOptSummary.lastScoreSummary.medianRate == null ? '—'
                                : `${autoOptSummary.lastScoreSummary.medianRate.toFixed(2)}%`} FB,
                              top {autoOptSummary.lastScoreSummary.topScoreMax == null ? '—'
                                : `${autoOptSummary.lastScoreSummary.topScoreMax.toFixed(2)}%`}.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </SettingsDrawer>
                )}
              </>
            )}
          </section>

          {configureOpen && setupAgent && (
            <section className="rounded-2xl border border-indigo-200 dark:border-indigo-800 bg-white dark:bg-gray-900 p-5">
              <AgentDesktopSetup
                agent={setupAgent}
                compact
                onAgentUpdated={() => {
                  reloadAgents({ silent: true, force: true });
                  loadDashboard({ light: false });
                }}
              />
            </section>
          )}

          {/* Progress strip */}
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: 'Follows sent', value: dash?.totals?.followsSent, hint: 'This live job' },
              { label: 'Available to follow', value: dash?.totals?.available, hint: 'Stored − followed − claimed' },
              { label: 'Claimed', value: dash?.totals?.claimedInSource, hint: 'Reserved now · released on pause/stop' },
              { label: 'Already followed', value: dash?.totals?.alreadyFollowed, hint: 'This workspace · this model' }
            ].map((card) => (
              <div
                key={card.label}
                className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-5 py-4"
              >
                <div className="text-xs uppercase tracking-wide text-gray-500">{card.label}</div>
                <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900 dark:text-white">
                  {fmt(card.value)}
                </div>
                <div className="text-xs text-gray-400 mt-1">{card.hint}</div>
              </div>
            ))}
          </section>

          {/* Daily goal */}
          <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
            <div className="flex items-center justify-between gap-3 mb-2">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Daily goal progress</h2>
              <span className="text-xs text-gray-500">
                {dash?.dailyGoal?.scopedToJob ? 'This live job' : 'Logged-in accounts on this VPS'}
              </span>
            </div>
            <DailyGoalMeter
              atCap={dash?.dailyGoal?.atCap}
              total={dash?.dailyGoal?.total}
            />
          </section>

          {/* Health */}
          <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Account health</h2>
            <div className="flex flex-wrap gap-2">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${statusTone('running')}`}
              >
                Active
                <span className="tabular-nums">{fmt(loggedInAccounts.length)}</span>
              </span>
            </div>
            <p className="mt-3 text-xs text-gray-500 flex items-start gap-1.5">
              <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
              Captcha and auth-expired stay on this VPS but are hidden here until the profile is logged in again.
            </p>
          </section>

          {/* Per-account */}
          <section
            ref={accountsSectionRef}
            className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden"
          >
            <button
              type="button"
              className="w-full flex items-center justify-between px-5 py-3 text-left"
              onClick={() => setShowAccounts((v) => !v)}
            >
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                {running || paused ? 'This job by user-profile' : 'Accounts for this run'}
              </h2>
              <span className="text-xs text-gray-500">
                {showAccounts ? 'Hide' : 'Show'}
                {' · '}
                {running || paused
                  ? `${fmt(liveRosterCount || dash?.dailyGoal?.total)} in this job`
                  : `${fmt(buildCounts.total)} of ${fmt(loggedInAccounts.length)} selected`}
              </span>
            </button>
            {showAccounts && (
              <div className="border-t border-gray-100 dark:border-gray-800">
                {!running && !paused && (
                  <div className="px-4 py-2.5 space-y-2 bg-gray-50 dark:bg-gray-800/40">
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="relative flex-1 min-w-[14rem]">
                        <MagnifyingGlassIcon className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="search"
                          className="form-input text-sm w-full !pl-8"
                          placeholder="Search or paste @usernames"
                          value={accountSearch}
                          onChange={(e) => setAccountSearch(e.target.value)}
                        />
                      </label>
                      <button
                        type="button"
                        className="btn-secondary !py-1 !px-2.5 text-xs"
                        disabled={!visibleIds.length}
                        onClick={() => selectVisibleAccounts(true)}
                      >
                        Select matches
                      </button>
                      <button
                        type="button"
                        className="btn-secondary !py-1 !px-2.5 text-xs"
                        disabled={!visibleIds.length}
                        onClick={() => selectVisibleAccounts(false)}
                      >
                        Deselect matches
                      </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                    <button
                      type="button"
                      className="btn-secondary !py-1 !px-2.5 text-xs"
                      onClick={selectAllRunAccounts}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="btn-secondary !py-1 !px-2.5 text-xs"
                      onClick={() => setRunAccountIds([])}
                    >
                      Select none
                    </button>
                    <span className="text-gray-400">|</span>
                    <button
                      type="button"
                      className="btn-secondary !py-1 !px-2.5 text-xs"
                      disabled={!runAccountIds.length}
                      onClick={() => assignBuildTo(FOLLOW_RUNNER_CLASSIC, runAccountIds)}
                    >
                      Selected → URL follow
                    </button>
                    <button
                      type="button"
                      className="btn-secondary !py-1 !px-2.5 text-xs"
                      disabled={!runAccountIds.length}
                      onClick={() => assignBuildTo(FOLLOW_RUNNER_V3, runAccountIds)}
                    >
                      Selected → Humanized 1
                    </button>
                    <button
                      type="button"
                      className="btn-secondary !py-1 !px-2.5 text-xs"
                      disabled={!runAccountIds.length}
                      onClick={() => assignBuildTo(FOLLOW_RUNNER_H2, runAccountIds)}
                    >
                      Selected → Humanized 2
                    </button>
                    <span className="text-gray-500 ml-auto">
                      {formatFollowBuildCounts(buildCounts)}
                      {searchTokens.length > 0 && (
                        <> · {fmt(visibleIds.length)} matching</>
                      )}
                    </span>
                    </div>
                  </div>
                )}
                {(running || paused) && (
                  <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/40">
                    <label className="relative block">
                      <MagnifyingGlassIcon className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="search"
                        className="form-input text-sm w-full !pl-8"
                        placeholder="Search @usernames in this job"
                        value={accountSearch}
                        onChange={(e) => setAccountSearch(e.target.value)}
                      />
                    </label>
                  </div>
                )}
                {accountsListExpanded && accountListCanFold && (
                  <AccountListFoldBar label="Show less" onClick={shrinkAccountList} />
                )}
                {(running || paused) ? (
                  <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    {!visibleLiveRunGroups.length && (
                      <p className="px-4 py-6 text-center text-gray-500 text-xs">
                        {searchTokens.length
                          ? `No usernames in this job match “${accountSearch.trim()}”.`
                          : 'No accounts in this live job yet.'}
                      </p>
                    )}
                    {shownLiveGroups.map((group) => (
                      <div key={group.profileId || '__none__'} className="p-4 space-y-3">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                              {group.name}
                            </h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {group.runner}
                              {' · '}
                              {fmt(group.accounts.length)} accounts
                              {' · '}
                              <span className="text-sky-600 dark:text-sky-400">
                                {fmt(group.sending)} sending
                              </span>
                              {' · '}
                              {fmt(group.followed)} follows this job
                            </p>
                          </div>
                        </div>
                        <DailyGoalMeter atCap={group.atCap} total={group.goalTotal} />
                        <div className="rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden max-h-[28rem] overflow-y-auto">
                          <LiveAccountTable
                            rows={group.visibleAccounts || group.accounts}
                            emptyLabel="No account rows for this user-profile yet."
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800/60 sticky top-0">
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                      {!running && !paused && (
                        <th className="px-3 py-2 font-medium w-10">
                          <input
                            type="checkbox"
                            className="form-checkbox"
                            checked={allShownSelected}
                            onChange={(e) => selectAccountIds(shownIds, e.target.checked)}
                            aria-label="Select accounts shown in the list"
                          />
                        </th>
                      )}
                      <th className="px-4 py-2 font-medium">Account</th>
                      <th className="px-4 py-2 font-medium">Follow build</th>
                      <th className="px-4 py-2 font-medium">Status</th>
                      <th className="px-4 py-2 font-medium">Followed</th>
                      <th className="px-4 py-2 font-medium">Budget left</th>
                      <th className="px-4 py-2 font-medium">Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {shownAccounts.map((row) => {
                      const id = String(row.accountId);
                      const inRun = runIdSet.has(id);
                      const runner = normalizeFollowRunner(config.accountFollowRunners?.[id]);
                      return (
                      <tr
                        key={row.accountId}
                        className={!running && !paused && !inRun ? 'opacity-50' : ''}
                      >
                        {!running && !paused && (
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              className="form-checkbox"
                              checked={inRun}
                              onChange={() => toggleRunAccount(id)}
                              aria-label={`Include @${row.username}`}
                            />
                          </td>
                        )}
                        <td className="px-4 py-2 font-mono">@{row.username}</td>
                        <td className="px-4 py-2">
                          {running || paused ? (
                            <span className="text-xs text-gray-600 dark:text-gray-300">
                              {labelFollowRunner(runner)}
                            </span>
                          ) : (
                            <select
                              className="form-select text-xs py-1"
                              value={runner}
                              disabled={!inRun}
                              onChange={(e) => assignBuildTo(e.target.value, [id])}
                            >
                              <option value={FOLLOW_RUNNER_CLASSIC}>URL follow</option>
                              <option value={FOLLOW_RUNNER_V3}>Humanized 1</option>
                              <option value={FOLLOW_RUNNER_H2}>Humanized 2</option>
                            </select>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${statusTone(row.jobStatus)}`}>
                            {row.jobStatus}
                          </span>
                        </td>
                        <td className="px-4 py-2 tabular-nums">
                          {fmt(row.followed)}
                          {row.budgetTotal > 0 && (
                            <span className="text-gray-400"> / {fmt(row.budgetTotal)}</span>
                          )}
                        </td>
                        <td className="px-4 py-2 tabular-nums">{fmt(row.budgetRemaining)}</td>
                        <td className="px-4 py-2 text-xs text-gray-500 max-w-[16rem] truncate" title={row.lastError || ''}>
                          {row.lastError || '—'}
                        </td>
                      </tr>
                      );
                    })}
                    {!loggedInAccounts.length && (
                      <tr>
                        <td colSpan={running || paused ? 6 : 7} className="px-4 py-6 text-center text-gray-500 text-xs">
                          {occupancy.loggedIn > 0
                            ? 'Tick a user-profile to load those accounts.'
                            : 'No logged-in accounts on this VPS.'}
                        </td>
                      </tr>
                    )}
                    {loggedInAccounts.length > 0 && !visibleAccounts.length && (
                      <tr>
                        <td colSpan={running || paused ? 6 : 7} className="px-4 py-6 text-center text-gray-500 text-xs">
                          No usernames match “{accountSearch.trim()}”.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                )}
                {accountListCanFold && (
                  <AccountListFoldBar
                    label={accountsListExpanded ? 'Show less' : 'See more'}
                    onClick={accountsListExpanded ? shrinkAccountList : () => setAccountsListExpanded(true)}
                  />
                )}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
